
import { Classification, SurgicalTemplate, Language, LocalPathology } from "@/contexts/types";
export const getOrthoDatabase = (lang: Language): LocalPathology[] => {
  // Helper for inline translation
  const t = <T extends any>(pt: T, en: T, es: T): T => {
    if (lang === 'en') return en;
    if (lang === 'es') return es;
    return pt;
  };

  return [
    // --- TRAUMA GERAL ---
    {
      "id": "ao_ota_general",
      "nome": t("Sistema AO/OTA (Universal)", "AO/OTA System (Universal)", "Sistema AO/OTA (Universal)"),
      "cid10": "T14.2",
      "synonyms": ["AO", "Muller", "Classificação Universal", "Alfa-numérica"],
      "taxonomy": {
        "anatomy": t(["Geral", "Esqueleto"], ["General", "Skeleton"], ["General", "Esqueleto"]),
        "pathology": ["Trauma", "Fratura"],
        "classification": ["AO/OTA"]
      },
      "resumo": t(
        "Sistema alfanumérico de 5 caracteres. 1º Dígito = Osso (1-Úmero, 2-Antebraço, 3-Fêmur, 4-Perna). 2º Dígito = Segmento (1-Proximal, 2-Diáfise, 3-Distal). Letra = Tipo (A, B, C).",
        "5-character alphanumeric system. 1st Digit = Bone (1-Humerus, 2-Forearm, 3-Femur, 4-Leg). 2nd Digit = Segment (1-Proximal, 2-Diaphysis, 3-Distal). Letter = Type (A, B, C).",
        "Sistema alfanumérico de 5 caracteres. 1º Dígito = Hueso (1-Húmero, 2-Antebrazo, 3-Fémur, 4-Pierna). 2º Dígito = Segmento (1-Proximal, 2-Diáfisis, 3-Distal). Letra = Tipo (A, B, C)."
      ),
      "imagemReferencia": "assets/db/ao_classification.jpg",
      "classificacaoDetalhada": [
        {
          "nome": "Tipos de Fratura (Padrão)",
          "tipos": [
            { "nome": "Tipo A", "descricao": t("Extra-articular (Metáfise) ou Simples (Diáfise). Traço único, <30° ou >30°.", "Extra-articular or Simple.", "Extra-articular o Simple.") },
            { "nome": "Tipo B", "descricao": t("Articular Parcial (Metáfise) ou Cunha (Diáfise). Contato mantido entre fragmentos principais.", "Partial Articular or Wedge.", "Articular Parcial o Cuña.") },
            { "nome": "Tipo C", "descricao": t("Articular Completa (Metáfise) ou Complexa/Cominutiva (Diáfise). Multifragmentária.", "Complete Articular or Complex.", "Articular Completa o Compleja.") }
          ]
        }
      ],
      "condutaConservadora": t("N/A - Depende do segmento específico.", "N/A - Depends on specific segment.", "N/A - Depende del segmento específico."),
      "condutaCirurgica": t("Base para planejamento. Tipos B e C geralmente requerem redução aberta e fixação interna (RAFI).", "Base for planning. Types B and C usually require ORIF.", "Base para planeamiento. Tipos B y C generalmente requieren RAFI."),
      "checklistMaterial": []
    },
    {
      "id": "open_fx",
      "nome": t("Fratura Exposta (Gustilo-Anderson)", "Open Fracture (Gustilo-Anderson)", "Fractura Abierta (Gustilo-Anderson)"),
      "cid10": "T14.1",
      "synonyms": ["Exposta", "Gustilo", "Trauma Aberto"],
      "taxonomy": {
        "anatomy": t(["Geral", "Membros"], ["General", "Limbs"], ["General", "Miembros"]),
        "pathology": ["Trauma", "Emergência"],
        "classification": ["Gustilo-Anderson"]
      },
      "resumo": t(
        "Classificação baseada na energia do trauma, extensão da lesão de partes moles e grau de contaminação.",
        "Classification based on energy, soft tissue injury, and contamination.",
        "Clasificación basada en energía, lesión de tejidos blandos y contaminación."
      ),
      "classificacaoDetalhada": [
        {
          "nome": t("Graus", "Grades", "Grados"),
          "tipos": [
            { "nome": "Grau I", "descricao": t("Ferida < 1cm, limpa, puntiforme.", "Wound < 1cm, clean, punctate.", "Herida < 1cm, limpia, puntiforme.") },
            { "nome": "Grau II", "descricao": t("Ferida 1-10cm, contusão de partes moles moderada.", "Wound 1-10cm, moderate soft tissue contusion.", "Herida 1-10cm, contusión de partes blandas moderada.") },
            { "nome": "Grau IIIA", "descricao": t("Ferida > 10cm, alta energia, cobertura óssea possível.", "Wound > 10cm, high energy, bone coverage possible.", "Herida > 10cm, alta energía, cobertura ósea posible.") },
            { "nome": "Grau IIIB", "descricao": t("Perda extensa de partes moles, requer retalho/cobertura.", "Extensive soft tissue loss, requires flap/coverage.", "Pérdida extensa de partes blandas, requiere colgajo/cobertura.") },
            { "nome": "Grau IIIC", "descricao": t("Lesão vascular com necessidade de reparo.", "Vascular injury requiring repair.", "Lesión vascular con necesidad de reparación.") }
          ]
        }
      ],
      "condutaConservadora": t("N/A - Sempre cirúrgico (Desbridamento).", "N/A - Always surgical (Debridement).", "N/A - Siempre quirúrgico (Desbridamiento)."),
      "condutaCirurgica": t("Antibioticoterapia imediata, profilaxia antitetânica, desbridamento cirúrgico urgente, estabilização (Fixador Externo ou Haste/Placa dependendo do grau).", "Immediate antibiotics, tetanus prophylaxis, urgent surgical debridement, stabilization.", "Antibioticoterapia inmediata, profilaxis antitetánica, desbridamiento quirúrgico urgente, estabilización."),
      "checklistMaterial": t(["Kit Fixador Externo", "Soro Fisiológico 9L", "Sistema de VAC"], ["External Fixator Kit", "Normal Saline 9L", "VAC System"], ["Kit Fijador Externo", "Suero Fisiológico 9L", "Sistema de VAC"])
    },

    // --- TRAUMA MEMBRO SUPERIOR ---
    {
      "id": "clavicula_fx",
      "nome": t("Fratura da Clavícula", "Clavicle Fracture", "Fractura de Clavícula"),
      "cid10": "S42.0",
      "synonyms": ["Clavícula", "Allman", "Craig"],
      "taxonomy": {
        "anatomy": t(["Ombro", "Clavícula"], ["Shoulder", "Clavicle"], ["Hombro", "Clavícula"]),
        "pathology": ["Trauma", "Fratura"],
        "classification": ["Allman"]
      },
      "resumo": t("Classificação de Allman divide em terços. Grupo I (Médio) é o mais comum (80%).", "Allman classification divides into thirds. Group I (Middle) is the most common (80%).", "Clasificación de Allman divide en tercios. Grupo I (Medio) es el más común (80%)."),
      "classificacaoDetalhada": [
        {
          "nome": t("Classificação de Allman", "Allman Classification", "Clasificación de Allman"),
          "tipos": [
            { "nome": t("Grupo I", "Group I", "Grupo I"), "descricao": t("Terço Médio (80%). Maioria conservador.", "Middle Third (80%). Mostly conservative.", "Tercio Medio (80%). Mayoría conservador.") },
            { "nome": t("Grupo II", "Group II", "Grupo II"), "descricao": t("Terço Lateral (15%). Subdivisão de Neer (estabilidade ligamento CC).", "Lateral Third (15%). Neer subdivision (CC ligament stability).", "Tercio Lateral (15%). Subdivisión de Neer (estabilidad ligamento CC).") },
            { "nome": t("Grupo III", "Group III", "Grupo III"), "descricao": t("Terço Medial (5%). Rara, associada a trauma torácico.", "Medial Third (5%). Rare, associated with thoracic trauma.", "Tercio Medial (5%). Rara, asociada a trauma torácico.") }
          ]
        }
      ],
      "condutaConservadora": t("Tipoia ou enfaixamento em 8 por 4-6 semanas. Indicado se sem desvio ou encurtamento < 2cm.", "Sling or figure-of-eight bandage for 4-6 weeks. Indicated if no displacement or shortening < 2cm.", "Cabestrillo o vendaje en 8 por 4-6 semanas. Indicado si no hay desplazamiento o acortamiento < 2cm."),
      "condutaCirurgica": t("RAFI com Placa ou Haste Flexível (TEN). Indicações: Exposta, iminência de exposição, lesão vascular, ombro flutuante, encurtamento > 2cm.", "ORIF with Plate or Flexible Nail (TEN). Indications: Open, impending exposure, vascular injury, floating shoulder, shortening > 2cm.", "RAFI con Placa o Clavo Flexible (TEN). Indicaciones: Expuesta, inminencia de exposición, lesión vascular, hombro flotante, acortamiento > 2cm."),
      "checklistMaterial": t(["Placa Bloqueada Clavícula", "Parafusos 3.5mm", "Haste TEN (opcional)"], ["Clavicle Locking Plate", "3.5mm Screws", "TEN Nail (optional)"], ["Placa Bloqueada Clavícula", "Tornillos 3.5mm", "Clavo TEN (opcional)"])
    },
    {
      "id": "prox_humerus_fx",
      "nome": t("Fratura do Úmero Proximal", "Proximal Humerus Fracture", "Fractura de Húmero Proximal"),
      "cid10": "S42.2",
      "synonyms": ["Neer", "Colo Cirúrgico", "Ombro"],
      "taxonomy": {
        "anatomy": t(["Ombro", "Úmero"], ["Shoulder", "Humerus"], ["Hombro", "Húmero"]),
        "pathology": ["Trauma", "Fratura"],
        "classification": ["Neer"]
      },
      "resumo": t(
        "Classificação de Neer baseada em 4 partes (Cabeça, Diáfise, Tubérculo Maior, Tubérculo Menor). Desvio considerado se > 1cm ou > 45°.",
        "Neer classification based on 4 parts (Head, Shaft, Greater Tuberosity, Lesser Tuberosity). Displacement if > 1cm or > 45°.",
        "Clasificación de Neer basada en 4 partes (Cabeza, Diáfisis, Troquiter, Troquín). Desplazamiento si > 1cm o > 45°."
      ),
      "classificacaoDetalhada": [
        {
          "nome": t("Classificação de Neer", "Neer Classification", "Clasificación de Neer"),
          "tipos": [
            { "nome": t("1 Parte", "1 Part", "1 Parte"), "descricao": t("Sem desvio significativo. (80% dos casos).", "No significant displacement. (80% of cases).", "Sin desplazamiento significativo. (80% de los casos).") },
            { "nome": t("2 Partes", "2 Parts", "2 Partes"), "descricao": t("Colo cirúrgico ou tubérculo isolado.", "Surgical neck or isolated tuberosity.", "Cuello quirúrgico o tuberosidad aislada.") },
            { "nome": t("3 Partes", "3 Parts", "3 Partes"), "descricao": t("Colo cirúrgico + 1 tubérculo.", "Surgical neck + 1 tuberosity.", "Cuello quirúrgico + 1 tuberosidad.") },
            { "nome": t("4 Partes", "4 Parts", "4 Partes"), "descricao": t("Cabeça desconectada de ambos tubérculos. Risco alto de necrose.", "Head disconnected from both tuberosities. High risk of necrosis.", "Cabeza desconectada de ambas tuberosidades. Alto riesgo de necrosis.") }
          ]
        }
      ],
      "condutaConservadora": t("Tipoia Velpeau e pendular precoce (1-2 sem). Para Neer 1 parte.", "Velpeau sling and early pendular exercises (1-2 weeks). For Neer 1 part.", "Cabestrillo Velpeau y ejercicios pendulares precoces (1-2 sem). Para Neer 1 parte."),
      "condutaCirurgica": t("Jovens: RAFI (Placa Philos). Idosos (3-4 partes): Hemiartroplastia ou Prótese Reversa.", "Young: ORIF (Philos Plate). Elderly (3-4 parts): Hemiarthroplasty or Reverse Prosthesis.", "Jóvenes: RAFI (Placa Philos). Ancianos (3-4 partes): Hemiartroplastia o Prótesis Reversa."),
      "checklistMaterial": t(["Placa Bloqueada Úmero Proximal", "Suturas Alta Resistência", "Prótese Reversa (Backup)"], ["Proximal Humerus Locking Plate", "High Resistance Sutures", "Reverse Prosthesis (Backup)"], ["Placa Bloqueada Húmero Proximal", "Suturas Alta Resistencia", "Prótesis Reversa (Backup)"])
    },
    {
      "id": "distal_radius_fx",
      "nome": t("Fratura do Rádio Distal", "Distal Radius Fracture", "Fractura de Radio Distal"),
      "cid10": "S52.5",
      "synonyms": ["Punho", "Colles", "Smith", "Barton", "Fernandez"],
      "taxonomy": {
        "anatomy": t(["Punho", "Rádio"], ["Wrist", "Radius"], ["Muñeca", "Radio"]),
        "pathology": t(["Trauma", "Fratura"], ["Trauma", "Fracture"], ["Trauma", "Fractura"]),
        "classification": ["Fernandez", "Frykman"]
      },
      "resumo": t(
        "Classificação de Fernandez baseada no mecanismo. Essencial para estabilidade.",
        "Fernandez classification based on mechanism. Essential for stability.",
        "Clasificación de Fernandez basada en el mecanismo. Esencial para la estabilidad."
      ),
      "classificacaoDetalhada": [
        {
          "nome": "Fernandez",
          "tipos": [
            { "nome": t("Tipo I", "Type I", "Tipo I"), "descricao": t("Flexão da metáfise (Colles/Smith). Ligamentos íntegros.", "Metaphyseal bending (Colles/Smith). Intact ligaments.", "Flexión de la metáfisis (Colles/Smith). Ligamentos íntegros.") },
            { "nome": t("Tipo II", "Type II", "Tipo II"), "descricao": t("Cisalhamento articular (Barton). Instável.", "Articular shearing (Barton). Unstable.", "Cizallamiento articular (Barton). Inestable.") },
            { "nome": t("Tipo III", "Type III", "Tipo III"), "descricao": t("Compressão (Die-punch). Afundamento articular.", "Compression (Die-punch). Articular depression.", "Compresión (Die-punch). Hundimiento articular.") },
            { "nome": t("Tipo IV", "Type IV", "Tipo IV"), "descricao": t("Avulsão ligamentar (Chauffeur). Fratura do estiloide.", "Ligamentous avulsion (Chauffeur). Styloid fracture.", "Avulsión ligamentaria (Chauffeur). Fractura de la estiloides.") },
            { "nome": t("Tipo V", "Type V", "Tipo V"), "descricao": t("Alta energia combinada/Explosão.", "Combined high energy/Explosion.", "Alta energía combinada/Explosión.") }
          ]
        }
      ],
      "condutaConservadora": t("Gesso Luva (Colles sem desvio). Redução incruenta se estável.", "Short arm cast (Colles without displacement). Closed reduction if stable.", "Yeso corto (Colles sin desplazamiento). Reducción cerrada si es estable."),
      "condutaCirurgica": t("Placa Volar Bloqueada (Padrão Ouro), Fios de Kirschner, Fixador Externo (cominuição grave).", "Volar Locking Plate (Gold Standard), Kirschner Wires, External Fixator (severe comminution).", "Placa Volar Bloqueada (Estándar de Oro), Agujas de Kirschner, Fijador Externo (conminución grave)."),
      "checklistMaterial": t(["Placa Volar Rádio Distal", "Parafusos 2.4/2.7mm", "Fios de Kirschner"], ["Volar Distal Radius Plate", "2.4/2.7mm Screws", "Kirschner Wires"], ["Placa Volar Radio Distal", "Tornillos 2.4/2.7mm", "Agujas de Kirschner"])
    },
    {
      "id": "scaphoid_fx",
      "nome": t("Fratura do Escafóide", "Scaphoid Fracture", "Fractura de Escafoides"),
      "cid10": "S62.0",
      "synonyms": ["Escafóide", "Tabaqueira Anatômica", "Herbert"],
      "taxonomy": {
        "anatomy": t(["Punho", "Mão", "Carpo"], ["Wrist", "Hand", "Carpus"], ["Muñeca", "Mano", "Carpo"]),
        "pathology": t(["Trauma", "Fratura"], ["Trauma", "Fracture"], ["Trauma", "Fractura"]),
        "classification": ["Herbert"]
      },
      "resumo": t(
        "Osso do carpo mais fraturado. Vascularização retrógrada (risco de necrose polo proximal). Classificação de Herbert.",
        "Most common carpal fracture. Retrograde blood supply (risk of proximal pole necrosis). Herbert classification.",
        "Hueso del carpo más fracturado. Vascularización retrógrada (riesgo de necrosis del polo proximal). Clasificación de Herbert."
      ),
      "classificacaoDetalhada": [
        {
          "nome": t("Classificação de Herbert", "Herbert Classification", "Clasificación de Herbert"),
          "tipos": [
            { "nome": t("Tipo A", "Type A", "Tipo A"), "descricao": t("Estável. (A1: Tubérculo, A2: Incompleta cintura).", "Stable. (A1: Tubercle, A2: Incomplete waist).", "Estable. (A1: Tubérculo, A2: Incompleta cintura).") },
            { "nome": t("Tipo B", "Type B", "Tipo B"), "descricao": t("Instável Aguda. (B1: Oblíqua, B2: Cintura completa, B3: Polo proximal).", "Acute Unstable. (B1: Oblique, B2: Complete waist, B3: Proximal pole).", "Inestable Aguda. (B1: Oblicua, B2: Cintura completa, B3: Polo proximal).") },
            { "nome": t("Tipo C", "Type C", "Tipo C"), "descricao": t("Retarde de Consolidação.", "Delayed Union.", "Retraso de Consolidación.") },
            { "nome": t("Tipo D", "Type D", "Tipo D"), "descricao": t("Pseudartrose estabelecida.", "Established Nonunion.", "Seudoartrosis establecida.") }
          ]
        }
      ],
      "condutaConservadora": t("Gesso Luva com inclusão do polegar (Spica) por 8-12 semanas. Para tipo A.", "Thumb spica cast for 8-12 weeks. For type A.", "Yeso con inclusión del pulgar (Spica) por 8-12 semanas. Para tipo A."),
      "condutaCirurgica": t("Parafuso de Compressão (Herbert/Acutrak) percutâneo ou aberto. Para tipos B, atletas ou desejo de retorno rápido.", "Compression Screw (Herbert/Acutrak) percutaneous or open. For type B, athletes, or fast return.", "Tornillo de Compresión (Herbert/Acutrak) percutáneo o abierto. Para tipos B, atletas o deseo de retorno rápido."),
      "checklistMaterial": t(["Parafuso Canulado de Compressão (Headless)", "Fio Guia", "Micro motor"], ["Headless Compression Screw", "Guide Wire", "Micro motor"], ["Tornillo Canulado de Compresión (Headless)", "Guía", "Micro motor"])
    },

    // --- MÃO E PUNHO ---
    {
      "id": "carpal_tunnel",
      "nome": t("Síndrome do Túnel do Carpo", "Carpal Tunnel Syndrome", "Síndrome del Túnel Carpiano"),
      "cid10": "G56.0",
      "synonyms": t(["STC", "Compressão Mediano", "Parestesia Mão"], ["CTS", "Median Compression", "Hand Paresthesia"], ["STC", "Compresión Mediano", "Parestesia Mano"]),
      "taxonomy": {
        "anatomy": t(["Punho", "Mão"], ["Wrist", "Hand"], ["Muñeca", "Mano"]),
        "pathology": t(["Compressiva", "Neuropatia"], ["Compressive", "Neuropathy"], ["Compresiva", "Neuropatía"]),
        "classification": [t("Clínica", "Clinical", "Clínica")]
      },
      "resumo": t(
        "Neuropatia compressiva mais comum. Nervo mediano. Testes: Phalen, Tinel, Durkan.",
        "Most common compressive neuropathy. Median nerve. Tests: Phalen, Tinel, Durkan.",
        "Neuropatía compresiva más común. Nervio mediano. Pruebas: Phalen, Tinel, Durkan."
      ),
      "classificacaoDetalhada": [
        {
          "nome": t("Estágios Clínicos", "Clinical Stages", "Estadios Clínicos"),
          "tipos": [
            { "nome": t("Leve", "Mild", "Leve"), "descricao": t("Sintomas intermitentes, noturnos. Sem perda sensitiva objetiva.", "Intermittent, nocturnal symptoms. No objective sensory loss.", "Síntomas intermitentes, nocturnos. Sin pérdida sensitiva objetiva.") },
            { "nome": t("Moderado", "Moderate", "Moderado"), "descricao": t("Sintomas persistentes, parestesia diurna. Phalen positivo rápido.", "Persistent symptoms, daytime paresthesia. Rapid positive Phalen.", "Síntomas persistentes, parestesia diurna. Phalen positivo rápido.") },
            { "nome": t("Grave", "Severe", "Grave"), "descricao": t("Atrofia tenar, perda sensitiva permanente (2 pontos > 6mm).", "Thenar atrophy, permanent sensory loss (2-point > 6mm).", "Atrofia tenar, pérdida sensitiva permanente (2 puntos > 6mm).") }
          ]
        }
      ],
      "condutaConservadora": t("Órtese noturna, AINEs, Infiltração de corticoide, Fisioterapia.", "Night splint, NSAIDs, Corticosteroid injection, Physiotherapy.", "Férula nocturna, AINEs, Infiltración de corticoides, Fisioterapia."),
      "condutaCirurgica": t("Liberação do Retináculo dos Flexores (Aberta ou Endoscópica). Indicada se falha conservadora ou atrofia.", "Flexor Retinaculum Release (Open or Endoscopic). Indicated if conservative failure or atrophy.", "Liberación del Retináculo de los Flexores (Abierta o Endoscópica). Indicada si falla el tratamiento conservador o hay atrofia."),
      "checklistMaterial": t(["Kit Pequena Cirurgia", "Lâmina de Bisturi 15", "Afastadores de Senn"], ["Minor Surgery Kit", "15 Blade", "Senn Retractors"], ["Kit Cirugía Menor", "Hoja de Bisturí 15", "Separadores de Senn"])
    },
    {
      "id": "trigger_finger",
      "nome": t("Dedo em Gatilho", "Trigger Finger", "Dedo en Resorte"),
      "cid10": "M65.3",
      "synonyms": t(["Tenossinovite Estenosante", "Gatilho"], ["Stenosing Tenosynovitis", "Trigger"], ["Tenosinovitis Estenosante", "Resorte"]),
      "taxonomy": {
        "anatomy": t(["Mão", "Dedos"], ["Hand", "Fingers"], ["Mano", "Dedos"]),
        "pathology": t(["Inflamatória", "Mecânica"], ["Inflammatory", "Mechanical"], ["Inflamatoria", "Mecánica"]),
        "classification": ["Quinnell"]
      },
      "resumo": t(
        "Estenose da polia A1 impedindo o deslizamento do tendão flexor.",
        "A1 pulley stenosis preventing flexor tendon gliding.",
        "Estenosis de la polea A1 que impide el deslizamiento del tendón flexor."
      ),
      "classificacaoDetalhada": [
        {
          "nome": t("Classificação de Quinnell (Green)", "Quinnell Classification (Green)", "Clasificación de Quinnell (Green)"),
          "tipos": [
            { "nome": t("Grau I", "Grade I", "Grado I"), "descricao": t("Dor e história de travamento, mas demonstrável no exame.", "Pain and history of catching, but not demonstrable on exam.", "Dolor e historia de bloqueo, pero no demostrable en el examen.") },
            { "nome": t("Grau II", "Grade II", "Grado II"), "descricao": t("Travamento demonstrável, extensão ativa possível.", "Demonstrable catching, active extension possible.", "Bloqueo demostrable, extensión activa posible.") },
            { "nome": t("Grau III", "Grade III", "Grado III"), "descricao": t("Travamento requer ajuda passiva para estender (A) ou fletir (B).", "Catching requires passive help to extend (A) or flex (B).", "Bloqueo requiere ayuda pasiva para extender (A) o flexionar (B).") },
            { "nome": t("Grau IV", "Grade IV", "Grado IV"), "descricao": t("Contratura em flexão fixa.", "Fixed flexion contracture.", "Contractura en flexión fija.") }
          ]
        }
      ],
      "condutaConservadora": t("AINEs, Órtese de extensão, Infiltração de corticoide (taxa de sucesso alta em não-diabéticos).", "NSAIDs, Extension splint, Corticosteroid injection (high success in non-diabetics).", "AINEs, Férula de extensión, Infiltración de corticoides (alta tasa de éxito en no diabéticos)."),
      "condutaCirurgica": t("Liberação percutânea (agulha) ou aberta da polia A1.", "Percutaneous (needle) or open A1 pulley release.", "Liberación percutánea (aguja) o abierta de la polea A1."),
      "checklistMaterial": t(["Agulha 18G (Percutânea)", "Tesoura íris (Aberta)"], ["18G Needle (Percutaneous)", "Iris Scissors (Open)"], ["Aguja 18G (Percutánea)", "Tijeras Iris (Abierta)"])
    },

    // --- COLUNA ---
    {
      "id": "lumbar_hernia",
      "nome": t("Hérnia Discal Lombar", "Lumbar Disc Herniation", "Hernia Discal Lumbar"),
      "cid10": "M51.1",
      "synonyms": t(["Hérnia de Disco", "Ciatalgia", "Radiculopatia"], ["Disc Herniation", "Sciatica", "Radiculopathy"], ["Hernia de Disco", "Ciática", "Radiculopatía"]),
      "taxonomy": {
        "anatomy": t(["Coluna", "Lombar"], ["Spine", "Lumbar"], ["Columna", "Lumbar"]),
        "pathology": t(["Degenerativa", "Compressiva"], ["Degenerative", "Compressive"], ["Degenerativa", "Compresiva"]),
        "classification": ["Macnab"]
      },
      "resumo": t(
        "Deslocamento do disco intervertebral comprimindo raiz nervosa. L4-L5 e L5-S1 mais comuns.",
        "Intervertebral disc displacement compressing nerve root. L4-L5 and L5-S1 most common.",
        "Desplazamiento del disco intervertebral comprimiendo la raíz nerviosa. L4-L5 y L5-S1 más comunes."
      ),
      "classificacaoDetalhada": [
        {
          "nome": t("Anatomopatológica", "Anatomopathological", "Anatomopatológica"),
          "tipos": [
            { "nome": t("Protrusão", "Protrusion", "Protrusión"), "descricao": t("Base larga, anel fibroso íntegro.", "Wide base, intact fibrous ring.", "Base ancha, anillo fibroso íntegro.") },
            { "nome": t("Extrusão", "Extrusion", "Extrusión"), "descricao": t("Base estreita, rompimento do anel, núcleo contido pelo ligamento longitudinal.", "Narrow base, ring rupture, nucleus contained by longitudinal ligament.", "Base estrecha, rotura del anillo, núcleo contenido por el ligamento longitudinal.") },
            { "nome": t("Sequestro", "Sequestration", "Secuestro"), "descricao": t("Fragmento livre no canal medular.", "Free fragment in the spinal canal.", "Fragmento libre en el canal medular.") }
          ]
        }
      ],
      "condutaConservadora": t("Repouso relativo, AINEs, Corticoide sistêmico, Gabapentina, Fisioterapia. 90% melhora em 6 semanas.", "Relative rest, NSAIDs, Systemic corticosteroids, Gabapentin, Physiotherapy. 90% improve in 6 weeks.", "Reposo relativo, AINEs, Corticoides sistémicos, Gabapentina, Fisioterapia. 90% mejora en 6 semanas."),
      "condutaCirurgica": t("Microdiscectomia. Indicações: Síndrome da Cauda Equina (Urgência), Déficit motor progressivo, Dor intratável > 6-8 semanas.", "Microdiscectomy. Indications: Cauda Equina Syndrome (Urgency), Progressive motor deficit, Intractable pain > 6-8 weeks.", "Microdiscectomía. Indicaciones: Síndrome de la Cola de Caballo (Urgencia), Déficit motor progresivo, Dolor intratable > 6-8 semanas."),
      "checklistMaterial": t(["Microscópio/Lupa", "Afastadores de Coluna", "Rongeurs"], ["Microscope/Loupes", "Spine Retractors", "Rongeurs"], ["Microscopio/Lupa", "Separadores de Columna", "Rongeurs"])
    },
    {
      "id": "spondylolisthesis",
      "nome": t("Espondilolistese", "Spondylolisthesis", "Espondilolistesis"),
      "cid10": "M43.1",
      "synonyms": t(["Listese", "Escorregamento Vertebral"], ["Listhesis", "Vertebral Slippage"], ["Listesis", "Desplazamiento Vertebral"]),
      "taxonomy": {
        "anatomy": t(["Coluna", "Lombar"], ["Spine", "Lumbar"], ["Columna", "Lumbar"]),
        "pathology": t(["Instabilidade", "Degenerativa"], ["Instability", "Degenerative"], ["Inestabilidad", "Degenerativa"]),
        "classification": ["Meyerding"]
      },
      "resumo": t(
        "Escorregamento de uma vértebra sobre a outra. Etiologias: Istmica (L5-S1, jovens), Degenerativa (L4-L5, idosos).",
        "Slippage of one vertebra over another. Etiologies: Isthmic (L5-S1, young), Degenerative (L4-L5, elderly).",
        "Desplazamiento de una vértebra sobre otra. Etiologías: Ístmica (L5-S1, jóvenes), Degenerativa (L4-L5, ancianos)."
      ),
      "classificacaoDetalhada": [
        {
          "nome": t("Classificação de Meyerding (Grau de Deslizamento)", "Meyerding Classification (Slippage Grade)", "Clasificación de Meyerding (Grado de Deslizamiento)"),
          "tipos": [
            { "nome": t("Grau I", "Grade I", "Grado I"), "descricao": t("< 25%", "< 25%", "< 25%") },
            { "nome": t("Grau II", "Grade II", "Grado II"), "descricao": t("25% - 50%", "25% - 50%", "25% - 50%") },
            { "nome": t("Grau III", "Grade III", "Grado III"), "descricao": t("50% - 75%", "50% - 75%", "50% - 75%") },
            { "nome": t("Grau IV", "Grade IV", "Grado IV"), "descricao": t("75% - 100%", "75% - 100%", "75% - 100%") },
            { "nome": t("Grau V", "Grade V", "Grado V"), "descricao": t("> 100% (Espondiloptose)", "> 100% (Spondyloptosis)", "> 100% (Espondiloptosis)") }
          ]
        }
      ],
      "condutaConservadora": t("Fortalecimento do Core, evitar hiperextensão. Analgesia.", "Core strengthening, avoid hyperextension. Analgesia.", "Fortalecimiento del Core, evitar hiperextensión. Analgesia."),
      "condutaCirurgica": t("Artrodese (Fusão) +/- Descompressão. Indicações: Progressão do escorregamento, déficit neurológico, falha conservadora.", "Arthrodesis (Fusion) +/- Decompression. Indications: Slippage progression, neurological deficit, conservative failure.", "Artrodesis (Fusión) +/- Descompresión. Indicaciones: Progresión del desplazamiento, déficit neurológico, falla conservadora."),
      "checklistMaterial": t(["Parafusos Pediculares", "Hastes", "Cages Intervertebrais (TLIF/PLIF)"], ["Pedicle Screws", "Rods", "Interbody Cages (TLIF/PLIF)"], ["Tornillos Pediculares", "Barras", "Cajas Intervertebrales (TLIF/PLIF)"])
    },

    // --- TRAUMA MEMBRO INFERIOR ---
    {
      "id": "femoral_neck_fx",
      "nome": t("Fratura do Colo do Fêmur", "Femoral Neck Fracture", "Fractura de Cuello Femoral"),
      "cid10": "S72.0",
      "synonyms": t(["Colo Femoral", "Garden", "Pauwels", "Intracapsular"], ["Femoral Neck", "Garden", "Pauwels", "Intracapsular"], ["Cuello Femoral", "Garden", "Pauwels", "Intracapsular"]),
      "taxonomy": {
        "anatomy": t(["Quadril", "Fêmur"], ["Hip", "Femur"], ["Cadera", "Fémur"]),
        "pathology": t(["Trauma", "Fratura"], ["Trauma", "Fracture"], ["Trauma", "Fractura"]),
        "classification": ["Garden", "Pauwels"]
      },
      "resumo": t(
        "Fratura intracapsular. Risco de necrose avascular da cabeça femoral. Classificação de Garden (Desvio) e Pauwels (Angulação).",
        "Intracapsular fracture. Risk of avascular necrosis of the femoral head. Garden (Displacement) and Pauwels (Angulation) classification.",
        "Fractura intracapsular. Riesgo de necrosis avascular de la cabeza femoral. Clasificación de Garden (Desplazamiento) y Pauwels (Angulación)."
      ),
      "classificacaoDetalhada": [
        {
          "nome": "Garden",
          "tipos": [
            { "nome": "Garden I", "descricao": t("Incompleta/Impactada em Valgo.", "Incomplete/Valgus Impacted.", "Incompleta/Impactada en Valgo.") },
            { "nome": "Garden II", "descricao": t("Completa sem desvio.", "Complete without displacement.", "Completa sin desplazamiento.") },
            { "nome": "Garden III", "descricao": t("Completa com desvio parcial (Varo).", "Complete with partial displacement (Varus).", "Completa con desplazamiento parcial (Varo).") },
            { "nome": "Garden IV", "descricao": t("Completa com desvio total (Perda de contato).", "Complete with total displacement (Loss of contact).", "Completa con desplazamiento total (Pérdida de contacto).") }
          ]
        }
      ],
      "condutaConservadora": t("Raramente indicada (apenas risco cirúrgico proibitivo).", "Rarely indicated (only prohibitive surgical risk).", "Raramente indicada (solo riesgo quirúrgico prohibitivo)."),
      "condutaCirurgica": t("Jovens (<60a): Redução Urgente + Fixação (DHS/Parafusos). Idosos: Garden I/II -> Fixação; Garden III/IV -> Artroplastia (Hemi ou Total).", "Young (<60y): Urgent Reduction + Fixation (DHS/Screws). Elderly: Garden I/II -> Fixation; Garden III/IV -> Arthroplasty.", "Jóvenes (<60a): Reducción Urgente + Fijación (DHS/Tornillos). Ancianos: Garden I/II -> Fijación; Garden III/IV -> Artroplastia."),
      "checklistMaterial": t(["Parafusos Canulados 7.3mm", "Prótese de Quadril (Cimentada/Não cimentada)"], ["7.3mm Cannulated Screws", "Hip Prosthesis (Cemented/Uncemented)"], ["Tornillos Canulados 7.3mm", "Prótesis de Cadera (Cementada/No cementada)"])
    },
    {
      "id": "intertroch_fx",
      "nome": t("Fratura Transtrocantérica", "Intertrochanteric Fracture", "Fractura Pertrocantérea"),
      "cid10": "S72.1",
      "synonyms": t(["Transtrocanteriana", "Intertrocantérica", "Tronzo"], ["Transtrochanteric", "Intertrochanteric", "Tronzo"], ["Transtrocantérica", "Intertrocantérica", "Tronzo"]),
      "taxonomy": {
        "anatomy": t(["Quadril", "Fêmur"], ["Hip", "Femur"], ["Cadera", "Fémur"]),
        "pathology": t(["Trauma", "Fratura"], ["Trauma", "Fracture"], ["Trauma", "Fractura"]),
        "classification": ["Tronzo", "AO"]
      },
      "resumo": t(
        "Extracapsular. Alta taxa de consolidação, risco baixo de necrose. Foco na estabilidade do implante.",
        "Extracapsular. High healing rate, low risk of necrosis. Focus on implant stability.",
        "Extracapsular. Alta tasa de consolidación, bajo riesgo de necrosis. Foco en la estabilidad del implante."
      ),
      "classificacaoDetalhada": [
        {
          "nome": t("Estabilidade (Evans/AO)", "Stability (Evans/AO)", "Estabilidad (Evans/AO)"),
          "tipos": [
            { "nome": t("Estável", "Stable", "Estable"), "descricao": t("Parede póstero-medial íntegra. Suporta carga após fixação.", "Intact posteromedial wall. Supports weight after fixation.", "Pared posteromedial íntegra. Soporta carga tras fijación.") },
            { "nome": t("Instável", "Unstable", "Inestable"), "descricao": t("Cominuição póstero-medial, traço reverso ou extensão subtrocantérica.", "Posteromedial comminution, reverse obliquity, or subtrochanteric extension.", "Conminución posteromedial, trazo reverso o extensión subtrocantérica.") }
          ]
        }
      ],
      "condutaConservadora": t("Não indicada (alta mortalidade por imobilismo).", "Not indicated (high mortality due to immobility).", "No indicada (alta mortalidad por inmovilidad)."),
      "condutaCirurgica": t("Estável: DHS (Dynamic Hip Screw). Instável: Haste Intramedular Cefalomedular (PFN/Gamma).", "Stable: DHS (Dynamic Hip Screw). Unstable: Cephalomedullary Nail (PFN/Gamma).", "Estable: DHS (Dynamic Hip Screw). Inestable: Clavo Intramedular Cefalomedular (PFN/Gamma)."),
      "checklistMaterial": t(["DHS (Placa-Tubo)", "Haste Cefalomedular Longa/Curta", "Mesa Ortopédica"], ["DHS (Plate-Tube)", "Long/Short Cephalomedullary Nail", "Orthopedic Table"], ["DHS (Placa-Tubo)", "Clavo Cefalomedular Largo/Corto", "Mesa Ortopédica"])
    },
    {
      "id": "femoral_shaft_fx",
      "nome": t("Fratura da Diáfise do Fêmur", "Femoral Shaft Fracture", "Fractura de Diáfisis Femoral"),
      "cid10": "S72.3",
      "synonyms": t(["Fêmur Diáfise", "Winquist"], ["Femoral Shaft", "Winquist"], ["Diáfisis Femoral", "Winquist"]),
      "taxonomy": {
        "anatomy": t(["Coxa", "Fêmur"], ["Thigh", "Femur"], ["Muslo", "Fémur"]),
        "pathology": t(["Trauma", "Fratura"], ["Trauma", "Fracture"], ["Trauma", "Fractura"]),
        "classification": ["Winquist-Hansen"]
      },
      "resumo": t(
        "Trauma de alta energia em jovens ou queda em idosos. Classificação de Winquist avalia cominuição.",
        "High energy trauma in young or fall in elderly. Winquist classification evaluates comminution.",
        "Trauma de alta energía en jóvenes o caída en ancianos. Clasificación de Winquist evalúa conminución."
      ),
      "classificacaoDetalhada": [
        {
          "nome": "Winquist-Hansen",
          "tipos": [
            { "nome": "Tipo I", "descricao": t("Sem cominuição ou mínima.", "No or minimal comminution.", "Sin conminución o mínima.") },
            { "nome": "Tipo II", "descricao": t("Cominuição < 50% da circunferência.", "Comminution < 50% of circumference.", "Conminución < 50% de la circunferencia.") },
            { "nome": "Tipo III", "descricao": t("Cominuição > 50% (Instabilidade rotacional).", "Comminution > 50% (Rotational instability).", "Conminución > 50% (Inestabilidad rotacional).") },
            { "nome": "Tipo IV", "descricao": t("Cominuição circunferencial (Segmentar/Explosão).", "Circumferential comminution (Segmental/Explosion).", "Conminución circunferencial (Segmentaria/Explosión).") }
          ]
        }
      ],
      "condutaConservadora": t("Tração esquelética (temporária).", "Skeletal traction (temporary).", "Tracción esquelética (temporal)."),
      "condutaCirurgica": t("Padrão Ouro: Haste Intramedular Bloqueada (Anterógrada ou Retrógrada). Placa se canal estreito ou prótese ipsilateral.", "Gold Standard: Locked Intramedullary Nail (Anterograde or Retrograde). Plate if narrow canal or ipsilateral prosthesis.", "Estándar de Oro: Clavo Intramedular Bloqueado (Anterógrado o Retrógrado). Placa si el canal es estrecho o prótesis ipsilateral."),
      "checklistMaterial": t(["Haste Intramedular Fêmur", "Fresas", "Bloqueios", "Mesa de Tração"], ["Femoral Intramedullary Nail", "Reamers", "Interlocks", "Traction Table"], ["Clavo Intramedular Femoral", "Fresas", "Bloqueos", "Mesa de Tracción"])
    },
    {
      "id": "tibial_plateau_fx",
      "nome": t("Fratura do Platô Tibial", "Tibial Plateau Fracture", "Fractura de Platillo Tibial"),
      "cid10": "S82.1",
      "synonyms": t(["Planalto Tibial", "Schatzker"], ["Tibial Plateau", "Schatzker"], ["Platillo Tibial", "Schatzker"]),
      "taxonomy": {
        "anatomy": t(["Joelho", "Tíbia"], ["Knee", "Tibia"], ["Rodilla", "Tibia"]),
        "pathology": t(["Trauma", "Fratura"], ["Trauma", "Fracture"], ["Trauma", "Fractura"]),
        "classification": ["Schatzker"]
      },
      "resumo": t(
        "Fratura articular da tíbia proximal. Risco de lesão meniscal e ligamentar. Classificação de Schatzker (I-VI).",
        "Articular fracture of proximal tibia. Risk of meniscal and ligamentous injury. Schatzker classification (I-VI).",
        "Fractura articular de la tibia proximal. Riesgo de lesión meniscal y ligamentaria. Clasificación de Schatzker (I-VI)."
      ),
      "classificacaoDetalhada": [
        {
          "nome": "Schatzker",
          "tipos": [
            { "nome": "I", "descricao": t("Cisalhamento lateral (Split).", "Lateral shearing (Split).", "Cizallamiento lateral (Split).") },
            { "nome": "II", "descricao": t("Cisalhamento + Depressão lateral.", "Shearing + Lateral depression.", "Cizallamiento + Depresión lateral.") },
            { "nome": "III", "descricao": t("Depressão pura lateral.", "Pure lateral depression.", "Depresión pura lateral.") },
            { "nome": "IV", "descricao": t("Platô Medial (Alta energia/Luxação).", "Medial Plateau (High energy/Dislocation).", "Platillo Medial (Alta energía/Luxación).") },
            { "nome": "V", "descricao": t("Bicondilar.", "Bicondylar.", "Bicondilar.") },
            { "nome": "VI", "descricao": t("Dissociação Metafisária.", "Metaphyseal Dissociation.", "Disociación Metafisaria.") }
          ]
        }
      ],
      "condutaConservadora": t("Sem desvio (<2mm degrau). Imobilização + Carga zero.", "Non-displaced (<2mm step). Immobilization + Non-weight bearing.", "Sin desplazamiento (<2mm escalón). Inmovilización + Carga cero."),
      "condutaCirurgica": t("RAFI com Placas de Suporte (Buttress) e Parafusos. Enxerto ósseo para afundamentos. Fixador externo se partes moles ruins (Schatzker VI).", "ORIF with Buttress Plates and Screws. Bone graft for depressions. External fixator if soft tissues are poor.", "RAFI con Placas de Soporte (Buttress) y Tornillos. Injerto óseo para hundimientos. Fijador externo si las partes blandas están mal."),
      "checklistMaterial": t(["Placa LCP Tíbia Proximal (Lat/Med)", "Enxerto Ósseo", "Parafusos Canulados"], ["Proximal Tibia LCP Plate (Lat/Med)", "Bone Graft", "Cannulated Screws"], ["Placa LCP Tibia Proximal (Lat/Med)", "Injerto Óseo", "Tornillos Canulados"])
    },
    {
      "id": "ankle_fx",
      "nome": t("Fratura do Tornozelo (Maléolos)", "Ankle Fracture", "Fractura de Tobillo"),
      "cid10": "S82.8",
      "synonyms": t(["Tornozelo", "Danis-Weber", "Lauge-Hansen", "Pott"], ["Ankle", "Danis-Weber", "Lauge-Hansen", "Pott"], ["Tobillo", "Danis-Weber", "Lauge-Hansen", "Pott"]),
      "taxonomy": {
        "anatomy": t(["Tornozelo", "Fíbula", "Tíbia"], ["Ankle", "Fibula", "Tibia"], ["Tobillo", "Peroné", "Tibia"]),
        "pathology": t(["Trauma", "Fratura"], ["Trauma", "Fracture"], ["Trauma", "Fractura"]),
        "classification": ["Danis-Weber", "Lauge-Hansen"]
      },
      "resumo": t(
        "Fratura mais comum de membro inferior. Avaliar estabilidade da sindesmose.",
        "Most common lower limb fracture. Evaluate syndesmotic stability.",
        "Fractura más común de miembro inferior. Evaluar estabilidad de la sindesmosis."
      ),
      "classificacaoDetalhada": [
        {
          "nome": t("Danis-Weber (Nível da fratura fibular)", "Danis-Weber (Fibular fracture level)", "Danis-Weber (Nivel de la fractura fibular)"),
          "tipos": [
            { "nome": "Tipo A", "descricao": t("Infrasindesmal. Sindesmose íntegra.", "Infrasyndesmotic. Intact syndesmosis.", "Infrasindesmal. Sindesmosis íntegra.") },
            { "nome": "Tipo B", "descricao": t("Transindesmal. 50% lesão sindesmose.", "Transsyndesmotic. 50% syndesmotic injury.", "Transindesmal. 50% lesión sindesmosis.") },
            { "nome": "Tipo C", "descricao": t("Suprasindesmal. 100% lesão sindesmose (Mais instável).", "Suprasyndesmotic. 100% syndesmotic injury (Most unstable).", "Suprasindesmal. 100% lesión sindesmosis (Más inestable).") }
          ]
        }
      ],
      "condutaConservadora": t("Weber A e Weber B estáveis (sem abertura medial). Bota gessada.", "Stable Weber A and Weber B (no medial opening). Walking cast.", "Weber A y Weber B estables (sin apertura medial). Bota de yeso."),
      "condutaCirurgica": t("Weber C, Weber B instável, Bimaleolar, Trimaleolar. RAFI Fíbula + Reparo Sindesmose (Parafuso ou Tightrope).", "Weber C, unstable Weber B, Bimalleolar, Trimalleolar. ORIF Fibula + Syndesmotic Repair.", "Weber C, Weber B inestable, Bimaleolar, Trimaleolar. RAFI Fíbula + Reparación Sindesmosis."),
      "checklistMaterial": t(["Placa 1/3 Tubular", "Parafusos 3.5mm", "Parafuso de Sindesmose/Tightrope"], ["1/3 Tubular Plate", "3.5mm Screws", "Syndesmotic Screw/Tightrope"], ["Placa 1/3 Tubular", "Tornillos 3.5mm", "Tornillo de Sindesmosis/Tightrope"])
    },
    {
      "id": "calcaneus_fx",
      "nome": t("Fratura do Calcâneo", "Calcaneal Fracture", "Fractura de Calcáneo"),
      "cid10": "S92.0",
      "synonyms": t(["Calcâneo", "Sanders", "Essex-Lopresti"], ["Calcaneus", "Sanders", "Essex-Lopresti"], ["Calcáneo", "Sanders", "Essex-Lopresti"]),
      "taxonomy": {
        "anatomy": t(["Pé", "Retropé"], ["Foot", "Hindfoot"], ["Pie", "Retropié"]),
        "pathology": t(["Trauma", "Fratura"], ["Trauma", "Fracture"], ["Trauma", "Fractura"]),
        "classification": ["Sanders"]
      },
      "resumo": t(
        "Intra-articular (75%). Classificação de Sanders baseada na TC (Faceta posterior). Ângulo de Böhler < 20° indica afundamento.",
        "Intra-articular (75%). Sanders classification based on CT (Posterior facet). Böhler angle < 20° indicates depression.",
        "Intra-articular (75%). Clasificación de Sanders basada en la TC (Faceta posterior). Ángulo de Böhler < 20° indica hundimiento."
      ),
      "classificacaoDetalhada": [
        {
          "nome": t("Sanders (Baseado na TC Coronal)", "Sanders (Based on Coronal CT)", "Sanders (Basado en la TC Coronal)"),
          "tipos": [
            { "nome": t("Tipo I", "Type I", "Tipo I"), "descricao": t("Sem desvio (<2mm).", "No displacement (<2mm).", "Sin desplazamiento (<2mm).") },
            { "nome": t("Tipo II", "Type II", "Tipo II"), "descricao": t("2 fragmentos (1 linha de fratura).", "2 fragments (1 fracture line).", "2 fragmentos (1 línea de fractura).") },
            { "nome": t("Tipo III", "Type III", "Tipo III"), "descricao": t("3 fragmentos (2 linhas com afundamento central).", "3 fragments (2 lines with central depression).", "3 fragmentos (2 líneas con hundimiento central).") },
            { "nome": t("Tipo IV", "Type IV", "Tipo IV"), "descricao": t("Cominutiva (>3 fragmentos).", "Comminuted (>3 fragments).", "Conminuta (>3 fragmentos).") }
          ]
        }
      ],
      "condutaConservadora": t("Tipo I, pacientes com vasculopatia/diabetes grave ou fumantes pesados (risco pele).", "Type I, patients with severe vasculopathy/diabetes or heavy smokers (skin risk).", "Tipo I, pacientes con vasculopatía/diabetes grave o fumadores pesados (riesgo de piel)."),
      "condutaCirurgica": t("RAFI (Acesso em L estendido ou Sinus Tarsi). Placa bloqueada específica. Restaurar altura e largura.", "ORIF (Extended L-approach or Sinus Tarsi). Specific locking plate. Restore height and width.", "RAFI (Acceso en L extendido o Sinus Tarsi). Placa bloqueada específica. Restaurar altura y anchura."),
      "checklistMaterial": t(["Placa Calcâneo Perímetro", "Parafusos de bloqueio", "Fios de Kirschner (redução)"], ["Calcaneal Perimeter Plate", "Locking Screws", "Kirschner Wires (reduction)"], ["Placa Calcáneo Perímetro", "Tornillos de bloqueo", "Agujas de Kirschner (reducción)"])
    },

    // --- MEDICINA ESPORTIVA ---
    {
      "id": "acl_tear",
      "nome": t("Rotura do LCA", "ACL Tear", "Rotura de LCA"),
      "cid10": "S83.5",
      "synonyms": t(["Ligamento Cruzado Anterior", "Entorse Joelho"], ["Anterior Cruciate Ligament", "Knee Sprain"], ["Ligamento Cruzado Anterior", "Esguince de Rodilla"]),
      "taxonomy": {
        "anatomy": t(["Joelho"], ["Knee"], ["Rodilla"]),
        "pathology": t(["Esporte", "Ligamento"], ["Sports", "Ligament"], ["Deporte", "Ligamento"]),
        "classification": ["Lachman", "Pivot-Shift"]
      },
      "resumo": t(
        "Lesão comum em pivô/desaceleração. Instabilidade anterior e rotacional. Diagnóstico clínico (Lachman) + RM.",
        "Common pivot/deceleration injury. Anterior and rotational instability. Clinical diagnosis (Lachman) + MRI.",
        "Lesión común en pivote/desaceleración. Inestabilidad anterior y rotacional. Diagnóstico clínico (Lachman) + RM."
      ),
      "classificacaoDetalhada": [
        {
          "nome": t("Gravidade", "Severity", "Gravedad"),
          "tipos": [
            { "nome": t("Parcial", "Partial", "Parcial"), "descricao": t("Fibras remanescentes, teste firme.", "Remaining fibers, firm test.", "Fibras remanentes, prueba firme.") },
            { "nome": t("Completa", "Complete", "Completa"), "descricao": t("Sem end-point (Lachman +++), Pivot Shift positivo.", "No end-point (Lachman +++), positive Pivot Shift.", "Sin end-point (Lachman +++), Pivot Shift positivo.") },
            { "nome": t("Avulsão", "Avulsion", "Avulsión"), "descricao": t("Arrancamento da espinha tibial (comum em crianças).", "Tibial spine avulsion (common in children).", "Arrancamiento de la espina tibial (común en niños).") }
          ]
        }
      ],
      "condutaConservadora": t("Idosos sedentários, artrose avançada, sem queixa de falseio.", "Sedentary elderly, advanced osteoarthritis, no instability complaints.", "Ancianos sedentarios, artrosis avanzada, sin quejas de inestabilidad."),
      "condutaCirurgica": t("Reconstrução LCA (Tendões Flexores ou Patelar). Jovens ativos ou sintomáticos.", "ACL Reconstruction (Hamstring or Patellar Tendon). Active or symptomatic young patients.", "Reconstrucción LCA (Tendones Flexores o Patelar). Jóvenes activos o sintomáticos."),
      "checklistMaterial": t(["Parafusos de Interferência", "Endobutton/Tightrope", "Lâmina de Shaver"], ["Interference Screws", "Endobutton/Tightrope", "Shaver Blade"], ["Tornillos de Interferencia", "Endobutton/Tightrope", "Hoja de Shaver"])
    },
    {
      "id": "meniscus_tear",
      "nome": t("Lesão de Menisco", "Meniscus Tear", "Lesión de Menisco"),
      "cid10": "S83.2",
      "synonyms": t(["Menisco", "Alça de Balde"], ["Meniscus", "Bucket Handle"], ["Menisco", "Asa de Cubo"]),
      "taxonomy": {
        "anatomy": t(["Joelho"], ["Knee"], ["Rodilla"]),
        "pathology": t(["Esporte", "Degenerativa"], ["Sports", "Degenerative"], ["Deporte", "Degenerativa"]),
        "classification": ["Morfológica"]
      },
      "resumo": t(
        "Dor na interlinha, bloqueio mecânico. Testes de McMurray e Apley. Zona vascularizada (Red-Red) tem potencial de cicatrização.",
        "Joint line pain, mechanical locking. McMurray and Apley tests. Vascular zone (Red-Red) has healing potential.",
        "Dolor en la interlínea, bloqueo mecánico. Pruebas de McMurray y Apley. La zona vascularizada (Red-Red) tiene potencial de cicatrización."
      ),
      "classificacaoDetalhada": [
        {
          "nome": t("Morfologia", "Morphology", "Morfología"),
          "tipos": [
            { "nome": t("Longitudinal / Vertical", "Longitudinal / Vertical", "Longitudinal / Vertical"), "descricao": t("Pode evoluir para Alça de Balde (Bloqueio).", "Can evolve into Bucket Handle (Locking).", "Puede evolucionar a Asa de Cubo (Bloqueo).") },
            { "nome": t("Radial", "Radial", "Radial"), "descricao": t("Interrompe função de carga.", "Disrupts load-bearing function.", "Interrumpe la función de carga.") },
            { "nome": t("Horizontal / Clivagem", "Horizontal / Cleavage", "Horizontal / Clivaje"), "descricao": t("Degenerativa.", "Degenerative.", "Degenerativa.") },
            { "nome": t("Complexa", "Complex", "Compleja"), "descricao": t("Múltiplos planos.", "Multiple planes.", "Múltiples planos.") }
          ]
        }
      ],
      "condutaConservadora": t("Lesões degenerativas, estáveis, periféricas pequenas.", "Degenerative, stable, small peripheral tears.", "Lesiones degenerativas, estables, periféricas pequeñas."),
      "condutaCirurgica": t("Sutura de Menisco (Jovens, zona vascular, alça de balde) ou Meniscectomia Parcial (Tecido inviável).", "Meniscal Repair (Young, vascular zone, bucket handle) or Partial Meniscectomy (Non-viable tissue).", "Sutura de Menisco (Jóvenes, zona vascular, asa de cubo) o Meniscectomía Parcial (Tejido inviable)."),
      "checklistMaterial": t(["Dispositivos de Sutura Meniscal (All-inside)", "Shaver", "Radiofrequência"], ["Meniscal Repair Devices (All-inside)", "Shaver", "Radiofrequency"], ["Dispositivos de Sutura Meniscal (All-inside)", "Shaver", "Radiofrecuencia"])
    },

    // --- ORTOPEDIA PEDIÁTRICA ---
    {
      "id": "ddh",
      "nome": t("Displasia do Desenvolvimento do Quadril (DDQ)", "Developmental Dysplasia of the Hip (DDH)", "Displasia del Desarrollo de la Cadera (DDC)"),
      "cid10": "Q65.8",
      "synonyms": t(["Luxação Congênita", "Barlow", "Ortolani", "Graf"], ["Congenital Dislocation", "Barlow", "Ortolani", "Graf"], ["Luxación Congénita", "Barlow", "Ortolani", "Graf"]),
      "taxonomy": {
        "anatomy": t(["Quadril", "Pediátrica"], ["Hip", "Pediatric"], ["Cadera", "Pediátrica"]),
        "pathology": t(["Congênita", "Desenvolvimento"], ["Congenital", "Developmental"], ["Congénita", "Desarrollo"]),
        "classification": ["Graf"]
      },
      "resumo": t(
        "Anormalidade no acetábulo/fêmur. Triagem com Barlow (luxável) e Ortolani (reduzível). USG (Graf) até 6 meses.",
        "Abnormality in acetabulum/femur. Screening with Barlow (luxatable) and Ortolani (reducible). Ultrasound (Graf) up to 6 months.",
        "Anormalidad en el acetábulo/fémur. Cribado con Barlow (luxable) y Ortolani (reducible). Ecografía (Graf) hasta los 6 meses."
      ),
      "classificacaoDetalhada": [
        {
          "nome": t("USG Graf", "Graf Ultrasound", "Ecografía de Graf"),
          "tipos": [
            { "nome": "Tipo I", "descricao": t("Normal (Alfa > 60°).", "Normal (Alpha > 60°).", "Normal (Alfa > 60°).") },
            { "nome": "Tipo IIa", "descricao": t("Imaturo (< 3 meses).", "Immature (< 3 months).", "Inmaduro (< 3 meses).") },
            { "nome": "Tipo IIb", "descricao": t("Displásico (> 3 meses, Alfa 50-60°).", "Dysplastic (> 3 months, Alpha 50-60°).", "Displásico (> 3 meses, Alfa 50-60°).") },
            { "nome": "Tipo D/III/IV", "descricao": t("Quadril descentrado/Luxado (Alfa < 43°).", "Decentered/Dislocated hip (Alpha < 43°).", "Cadera descentrada/Luxada (Alfa < 43°).") }
          ]
        }
      ],
      "condutaConservadora": t("Suspensório de Pavlik (0-6 meses) com 90% sucesso. Gesso pélvico se falha.", "Pavlik Harness (0-6 months) with 90% success. Spica cast if failure.", "Arnés de Pavlik (0-6 meses) con 90% de éxito. Yeso pélvico si falla."),
      "condutaCirurgica": t("Redução Cruenta + Osteotomia (Pélvica/Femoral) se diagnóstico tardio (>18 meses) ou falha conservadora.", "Open Reduction + Osteotomy (Pelvic/Femoral) if late diagnosis (>18 months) or conservative failure.", "Reducción Abierta + Osteotomía (Pélvica/Femoral) si el diagnóstico es tardío (>18 meses) o falla conservadora."),
      "checklistMaterial": t(["Suspensório de Pavlik", "Mesa de Gesso"], ["Pavlik Harness", "Spica Table"], ["Arnés de Pavlik", "Mesa de Yeso"])
    },
    {
      "id": "clubfoot",
      "nome": t("Pé Boto Congênito", "Congenital Clubfoot", "Pie Zambo"),
      "cid10": "Q66.0",
      "synonyms": t(["PTC", "Pé Torto", "Ponseti"], ["Clubfoot", "Ponseti"], ["Pie Zambo", "Ponseti"]),
      "taxonomy": {
        "anatomy": t(["Pé", "Pediátrica"], ["Foot", "Pediatric"], ["Pie", "Pediátrica"]),
        "pathology": t(["Congênita", "Deformidade"], ["Congenital", "Deformity"], ["Congénita", "Deformidad"]),
        "classification": ["Pirani", "Dimeglio"]
      },
      "resumo": t(
        "Deformidade CAVE (Cavo, Aduto, Varo, Equino). Padrão Ouro: Método de Ponseti.",
        "CAVE deformity (Cavus, Adductus, Varus, Equinus). Gold Standard: Ponseti Method.",
        "Deformidad CAVE (Cavo, Adductus, Varo, Equino). Estándar de Oro: Método de Ponseti."
      ),
      "classificacaoDetalhada": [
        {
          "nome": t("Componentes CAVE", "CAVE Components", "Componentes CAVE"),
          "tipos": [
            { "nome": t("Cavo", "Cavus", "Cavo"), "descricao": t("Elevação do arco plantar (1º a corrigir).", "Elevation of plantar arch (1st to correct).", "Elevación del arco plantar (1º a corregir).") },
            { "nome": t("Aduto", "Adductus", "Adductus"), "descricao": t("Metatarso desviado medialmente.", "Metatarsus medially deviated.", "Metatarso desviado medialmente.") },
            { "nome": t("Varo", "Varus", "Varo"), "descricao": t("Retropé invertido.", "Inverted hindfoot.", "Retropié invertido.") },
            { "nome": t("Equino", "Equinus", "Equino"), "descricao": t("Flexão plantar fixa (Último a corrigir).", "Fixed plantar flexion (Last to correct).", "Flexión plantar fija (Último a corregir).") }
          ]
        }
      ],
      "condutaConservadora": t("Método de Ponseti: Gessos seriados semanais (5-7 trocas). Tenotomia do Aquiles percutânea para corrigir equino residual.", "Ponseti Method: Weekly serial casts (5-7 changes). Percutaneous Achilles tenotomy for residual equinus.", "Método de Ponseti: Yesos seriados semanales (5-7 cambios). Tenotomía de Aquiles percutánea para corregir el equino residual."),
      "condutaCirurgica": t("Liberação Póstero-Medial (Turco) - Apenas se falha grave do Ponseti (Raro hoje).", "Posteromedial Release (Turco) - Only if severe Ponseti failure (Rare today).", "Liberación Postero-Medial (Turco) - Solo si falla grave de Ponseti (Raro hoy)."),
      "checklistMaterial": t(["Gesso", "Algodão ortopédico", "Bisturi 11 (Tenotomia)", "Órtese de Dennis-Brown (Manutenção)"], ["Cast", "Orthopedic cotton", "Scalpel 11 (Tenotomy)", "Dennis-Brown Brace (Maintenance)"], ["Yeso", "Algodón ortopédico", "Bisturí 11 (Tenotomía)", "Órtesis de Dennis-Brown (Mantenimiento)"])
    },
    {
      "id": "supracondylar_fx",
      "nome": t("Fratura Supracondiliana do Úmero", "Supracondylar Humerus Fracture", "Fractura Supracondílea"),
      "cid10": "S42.4",
      "synonyms": t(["Cotovelo Pediátrico", "Gartland"], ["Pediatric Elbow", "Gartland"], ["Codo Pediátrico", "Gartland"]),
      "taxonomy": {
        "anatomy": t(["Cotovelo", "Pediátrica"], ["Elbow", "Pediatric"], ["Codo", "Pediátrica"]),
        "pathology": t(["Trauma", "Fratura"], ["Trauma", "Fracture"], ["Trauma", "Fractura"]),
        "classification": ["Gartland"]
      },
      "resumo": t(
        "Fratura mais comum do cotovelo em crianças (5-7 anos). Risco neurovascular (AIN, Radial) e Síndrome de Volkmann.",
        "Most common elbow fracture in children (5-7 years). Neurovascular risk (AIN, Radial) and Volkmann Syndrome.",
        "Fractura más común del codo en niños (5-7 años). Riesgo neurovascular (Nervio Interóseo Anterior, Radial) y Síndrome de Volkmann."
      ),
      "classificacaoDetalhada": [
        {
          "nome": t("Gartland (Extensão)", "Gartland (Extension)", "Gartland (Extensión)"),
          "tipos": [
            { "nome": "Tipo I", "descricao": t("Sem desvio. Linha umeral anterior cruza o capítulo.", "Non-displaced. Anterior humeral line crosses the capitellum.", "Sin desplazamiento. Línea humeral anterior cruza el capítulo.") },
            { "nome": "Tipo II", "descricao": t("Desvio parcial. Córtex posterior íntegro.", "Partial displacement. Intact posterior cortex.", "Desplazamiento parcial. Córtex posterior íntegro.") },
            { "nome": "Tipo III", "descricao": t("Desvio total. Sem contato cortical.", "Total displacement. No cortical contact.", "Desplazamiento total. Sin contacto cortical.") },
            { "nome": "Tipo IV", "descricao": t("Instabilidade multidirecional (diagnóstico intra-op).", "Multidirectional instability (intra-op diagnosis).", "Inestabilidad multidireccional (diagnóstico intraoperatorio).") }
          ]
        }
      ],
      "condutaConservadora": t("Gesso Axilo-Palmar a 90° (Apenas Tipo I).", "Long arm cast at 90° (Type I only).", "Yeso braquiopalmar a 90° (Solo Tipo I)."),
      "condutaCirurgica": t("Redução Fechada + Fixação Percutânea com Fios de Kirschner (Cruzados ou Divergentes Laterais). Urgência se mão pálida/sem pulso.", "Closed Reduction + Percutaneous Pinning with Kirschner Wires (Crossed or Lateral Divergent). Urgency if pale hand/no pulse.", "Reducción Cerrada + Fijación Percutánea con Agujas de Kirschner (Cruzadas o Divergentes Laterales). Urgencia si la mano está pálida/sin pulso."),
      "checklistMaterial": t(["Fios de Kirschner 1.5/2.0mm", "Intensificador de Imagem"], ["Kirschner Wires 1.5/2.0mm", "Image Intensifier"], ["Agujas de Kirschner 1.5/2.0mm", "Intensificador de Imagen"])
    },
    {
      "id": "scfe",
      "nome": t("Epifisiólise do Fémur Proximal", "Slipped Capital Femoral Epiphysis (SCFE)", "Epifisiólisis Femoral Proximal"),
      "cid10": "M91.1",
      "synonyms": t(["Escorregamento Epifisário", "Epifisiolistese"], ["SCFE", "Epiphysiolisthesis"], ["Deslizamiento Epifisario", "Epifisiolistesis"]),
      "taxonomy": {
        "anatomy": t(["Quadril", "Pediátrica"], ["Hip", "Pediatric"], ["Cadera", "Pediátrica"]),
        "pathology": t(["Endócrina", "Mecânica"], ["Endocrine", "Mechanical"], ["Endocrina", "Mecánica"]),
        "classification": ["Loder"]
      },
      "resumo": t(
        "Deslizamento da cabeça femoral em adolescentes obesos. Dor no joelho pode ser referida.",
        "Sliding of the femoral head in obese adolescents. Knee pain may be referred.",
        "Deslizamiento de la cabeza femoral en adolescentes obesos. El dolor en la rodilla puede ser referido."
      ),
      "classificacaoDetalhada": [
        {
          "nome": t("Estabilidade (Loder)", "Stability (Loder)", "Estabilidad (Loder)"),
          "tipos": [
            { "nome": t("Estável", "Stable", "Estable"), "descricao": t("Consegue deambular (com ou sem muletas). Baixo risco necrose.", "Able to walk (with or without crutches). Low necrosis risk.", "Puede deambular (con o sin muletas). Bajo riesgo de necrosis.") },
            { "nome": t("Instável", "Unstable", "Inestable"), "descricao": t("Incapaz de deambular/apoiar. Alto risco de necrose avascular (50%).", "Unable to walk/bear weight. High risk of avascular necrosis (50%).", "Incapaz de deambular/apoyar. Alto riesgo de necrosis avascular (50%).") }
          ]
        }
      ],
      "condutaConservadora": t("Não existe. Diagnóstico = Cirurgia.", "None. Diagnosis = Surgery.", "No existe. Diagnóstico = Cirugía."),
      "condutaCirurgica": t("Fixação In Situ com 1 parafuso canulado (evitar redução forçada).", "In Situ Fixation with 1 cannulated screw (avoid forced reduction).", "Fijación In Situ con 1 tornillo canulado (evitar reducción forzada)."),
      "checklistMaterial": t(["Parafuso Canulado 6.5/7.3mm (Rosca parcial 16mm)"], ["Cannulated Screw 6.5/7.3mm (Partial thread 16mm)"], ["Tornillo Canulado 6.5/7.3mm (Rosca parcial 16mm)"])
    },

    // --- INFECÇÃO, TUMORES E GERAL ---
    {
      "id": "septic_arthritis",
      "nome": t("Artrite Séptica", "Septic Arthritis", "Artritis Séptica"),
      "cid10": "M00.9",
      "synonyms": t(["Pioartrite", "Infecção Articular", "Kocher"], ["Septic Arthritis", "Joint Infection", "Kocher"], ["Artritis Séptica", "Infección Articular", "Kocher"]),
      "taxonomy": {
        "anatomy": t(["Geral", "Articulação"], ["General", "Joint"], ["General", "Articulación"]),
        "pathology": t(["Infecção", "Urgência"], ["Infection", "Urgency"], ["Infección", "Urgencia"]),
        "classification": ["Kocher"]
      },
      "resumo": t(
        "Emergência ortopédica. Destruição da cartilagem em horas. Critérios de Kocher ajudam no diagnóstico em crianças.",
        "Orthopedic emergency. Cartilage destruction in hours. Kocher criteria help diagnosis in children.",
        "Emergencia ortopédica. Destrucción del cartílago en horas. Los criterios de Kocher ayudan al diagnóstico en niños."
      ),
      "classificacaoDetalhada": [
        {
          "nome": t("Critérios de Kocher (Probabilidade)", "Kocher Criteria (Probability)", "Criterios de Kocher (Probabilidad)"),
          "tipos": [
            { "nome": t("1. Febre", "1. Fever", "1. Fiebre"), "descricao": "> 38.5°C" },
            { "nome": t("2. Não apoio", "2. Non-weight bearing", "2. No apoyo"), "descricao": t("Claudicação ou recusa de marcha.", "Limping or refusal to walk.", "Claudicación o rechazo a la marcha.") },
            { "nome": t("3. VHS", "3. ESR", "3. VSG"), "descricao": "> 40 mm/h" },
            { "nome": t("4. Leucocitose", "4. Leukocytosis", "4. Leucocitosis"), "descricao": "> 12.000" },
            { "nome": t("Interpretação", "Interpretation", "Interpretación"), "descricao": t("4 critérios = 99% chance de artrite séptica.", "4 criteria = 99% chance of septic arthritis.", "4 criterios = 99% de probabilidad de artritis séptica.") }
          ]
        }
      ],
      "condutaConservadora": t("Apenas antibiótico se dúvida diagnóstica inicial (punção negativa), mas drenagem é mandatória se pus.", "Antibiotics only if initial diagnostic doubt (negative tap), but drainage is mandatory if pus.", "Solo antibiótico si hay duda diagnóstica inicial (punción negativa), pero el drenaje es obligatorio si hay pus."),
      "condutaCirurgica": t("Artrotomia/Artroscopia para lavagem e desbridamento urgente + Antibiótico IV.", "Arthrotomy/Arthroscopy for urgent irrigation and debridement + IV Antibiotics.", "Artrotomía/Artroscopia para lavado y desbridamiento urgente + Antibiótico IV."),
      "checklistMaterial": t(["Kit de Lavagem", "Soro 9L", "Dreno de sucção"], ["Irrigation Kit", "9L Saline", "Suction Drain"], ["Kit de Lavado", "Suero 9L", "Drenaje de succión"])
    },
    {
      "id": "osteomyelitis",
      "nome": t("Osteomielite", "Osteomyelitis", "Osteomielitis"),
      "cid10": "M86",
      "synonyms": t(["Infecção Óssea", "Cierny-Mader"], ["Bone Infection", "Cierny-Mader"], ["Infección Ósea", "Cierny-Mader"]),
      "taxonomy": {
        "anatomy": t(["Geral", "Osso"], ["General", "Bone"], ["General", "Hueso"]),
        "pathology": t(["Infecção"], ["Infection"], ["Infección"]),
        "classification": ["Cierny-Mader"]
      },
      "resumo": t(
        "Infecção do osso. Classificação de Cierny-Mader guia tratamento baseada na anatomia e hospedeiro.",
        "Bone infection. Cierny-Mader classification guides treatment based on anatomy and host.",
        "Infección del hueso. La clasificación de Cierny-Mader guía el tratamiento basada en la anatomía y el huésped."
      ),
      "classificacaoDetalhada": [
        {
          "nome": t("Cierny-Mader (Anatômico)", "Cierny-Mader (Anatomic)", "Cierny-Mader (Anatómico)"),
          "tipos": [
            { "nome": t("Estágio 1", "Stage 1", "Estadio 1"), "descricao": t("Medular (Endosteal).", "Medullary (Endosteal).", "Medular (Endosteal).") },
            { "nome": t("Estágio 2", "Stage 2", "Estadio 2"), "descricao": t("Superficial (Cortical).", "Superficial (Cortical).", "Superficial (Cortical).") },
            { "nome": t("Estágio 3", "Stage 3", "Estadio 3"), "descricao": t("Localizada (Sequestro + Cavidade).", "Localized (Sequestrum + Cavity).", "Localizada (Secuestro + Cavidad).") },
            { "nome": t("Estágio 4", "Stage 4", "Estadio 4"), "descricao": t("Difusa (Instabilidade mecânica).", "Diffuse (Mechanical instability).", "Difusa (Inestabilidad mecánica).") }
          ]
        }
      ],
      "condutaConservadora": t("Antibioticoterapia prolongada (apenas se hospedeiro C - sem condições cirúrgicas).", "Prolonged antibiotic therapy (only if host C - no surgical conditions).", "Antibioticoterapia prolongada (solo si el huésped es C - sin condiciones quirúrgicas)."),
      "condutaCirurgica": t("Desbridamento radical, remoção de sequestros, preenchimento de espaço morto (Cimento c/ ATB), estabilização.", "Radical debridement, removal of sequestra, dead space management (Antibiotic cement), stabilization.", "Desbridamiento radical, eliminación de secuestros, manejo del espacio muerto (Cemento con ATB), estabilización."),
      "checklistMaterial": t(["Cimento Ósseo com Antibiótico", "Pérolas de PMMA", "Fresas de Reamer"], ["Antibiotic Bone Cement", "PMMA Beads", "Reamer Bits"], ["Cemento Óseo con Antibiótico", "Perlas de PMMA", "Fresas de Reamer"])
    },
    {
      "id": "osteosarcoma",
      "nome": t("Osteossarcoma", "Osteosarcoma", "Osteosarcoma"),
      "cid10": "C40",
      "synonyms": t(["Tumor Ósseo", "Sarcoma", "Enneking"], ["Bone Tumor", "Sarcoma", "Enneking"], ["Tumor Óseo", "Sarcoma", "Enneking"]),
      "taxonomy": {
        "anatomy": t(["Geral", "Metáfise"], ["General", "Metaphysis"], ["General", "Metáfisis"]),
        "pathology": t(["Tumor", "Maligno"], ["Tumor", "Malignant"], ["Tumor", "Maligno"]),
        "classification": ["Enneking"]
      },
      "resumo": t(
        "Tumor ósseo maligno primário mais comum em jovens. Triângulo de Codman e raios de sol no RX.",
        "Most common primary malignant bone tumor in young people. Codman triangle and sunburst pattern on X-ray.",
        "Tumor óseo maligno primario más común en jóvenes. Triángulo de Codman y rayos de sol en el RX."
      ),
      "classificacaoDetalhada": [
        {
          "nome": t("Estadiamento Enneking (Malignos)", "Enneking Staging (Malignant)", "Estadificación de Enneking (Malignos)"),
          "tipos": [
            { "nome": "I", "descricao": t("Baixo grau (A: Intracompartimental, B: Extracompartimental).", "Low grade (A: Intracompartmental, B: Extracompartmental).", "Bajo grado (A: Intracompartimental, B: Extracompartimental).") },
            { "nome": "II", "descricao": t("Alto grau (A: Intra, B: Extra).", "High grade (A: Intra, B: Extra).", "Alto grado (A: Intra, B: Extra).") },
            { "nome": "III", "descricao": t("Metástase presente (Qualquer grau).", "Metastasis present (Any grade).", "Metástasis presente (Cualquier grado).") }
          ]
        }
      ],
      "condutaConservadora": t("Quimioterapia Neoadjuvante.", "Neoadjuvant Chemotherapy.", "Quimioterapia Neoadyuvante."),
      "condutaCirurgica": t("Ressecção ampla com margens livres (Cirurgia de Salvamento de Membro ou Amputação) + Quimio Adjuvante.", "Wide resection with free margins (Limb Salvage Surgery or Amputation) + Adjuvant Chemo.", "Resección amplia con márgenes libres (Cirugía de Salvamento de Miembro o Amputación) + Quimio Adyuvante."),
      "checklistMaterial": t(["Prótese Não-Convencional (Tumor)", "Serra Oscilatória"], ["Non-Conventional Prosthesis (Tumor)", "Oscillating Saw"], ["Prótesis No Convencional (Tumor)", "Sierra Oscilatoria"])
    },
    {
      "id": "hallux_valgus",
      "nome": t("Hálux Valgo", "Hallux Valgus", "Hallux Valgus"),
      "cid10": "M20.1",
      "synonyms": t(["Joanete", "Bunion"], ["Bunion", "Hallux Valgus"], ["Juanete", "Bunion"]),
      "taxonomy": {
        "anatomy": t(["Pé"], ["Foot"], ["Pie"]),
        "pathology": t(["Deformidade", "Degenerativa"], ["Deformity", "Degenerative"], ["Deformidad", "Degenerativa"]),
        "classification": ["Mann"]
      },
      "resumo": t(
        "Desvio lateral do hálux e varo do 1º metatarso. Tratamento cirúrgico baseado nos ângulos.",
        "Lateral deviation of the hallux and varus of the 1st metatarsus. Surgical treatment based on angles.",
        "Desviación lateral del hallux y varo del 1er metatarso. Tratamiento quirúrgico basado en los ángulos."
      ),
      "classificacaoDetalhada": [
        {
          "nome": t("Gravidade Radiográfica", "Radiographic Severity", "Gravedad Radiográfica"),
          "tipos": [
            { "nome": t("Leve", "Mild", "Leve"), "descricao": t("Ângulo HV < 30°, Intermetatarsal < 13°. Congruente.", "HV Angle < 30°, Intermetatarsal < 13°. Congruent.", "Ángulo HV < 30°, Intermetatarsal < 13°. Congruente.") },
            { "nome": t("Moderado", "Moderate", "Moderado"), "descricao": t("Ângulo HV 30-40°, IM 13-20°. Subluxado.", "HV Angle 30-40°, IM 13-20°. Subluxated.", "Ángulo HV 30-40°, IM 13-20°. Subluxado.") },
            { "nome": t("Grave", "Severe", "Grave"), "descricao": t("Ângulo HV > 40°, IM > 20°.", "HV Angle > 40°, IM > 20°.", "Ángulo HV > 40°, IM > 20°.") }
          ]
        }
      ],
      "condutaConservadora": t("Calçados largos, espaçadores (sintomático).", "Wide shoes, spacers (symptomatic).", "Calzado ancho, espaciadores (sintomático)."),
      "condutaCirurgica": t("Leve: Chevron/Austin distal. Moderado: Scarf (Diáfise). Grave: Lapidus (Artrodese TMT) ou Osteotomia Base.", "Mild: Distal Chevron/Austin. Moderate: Scarf (Diaphysis). Severe: Lapidus (TMT Arthrodesis) or Base Osteotomy.", "Leve: Chevron/Austin distal. Moderado: Scarf (Diáfisis). Grave: Lapidus (Artrodesis TMT) u Osteotomía de Base."),
      "checklistMaterial": t(["Parafusos Herbert", "Placa de Lapidus", "Serra micro-sagital"], ["Herbert Screws", "Lapidus Plate", "Micro-sagittal Saw"], ["Tornillos Herbert", "Placa de Lapidus", "Sierra micro-sagital"])
    }
  ];
};

export const INITIAL_ORTHO_DATABASE = getOrthoDatabase('pt');

export const getSurgicalTemplates = (lang: Language): SurgicalTemplate[] => {
  const t = <T extends any>(pt: T, en: T, es: T): T => {
    if (lang === 'en') return en;
    if (lang === 'es') return es;
    return pt;
  };

  return [
    {
      id: 'general_consult',
      title: t('Consulta Geral / Anamnese', 'General Consult / History', 'Consulta General / Anamnesis'),
      description: t('Estrutura padrão de atendimento.', 'Standard care structure.', 'Estructura estándar de atención.'),
      defaultText: t(
        'QP: \nHDA: \nHP: \nMedicamentos: \nExame Físico: \nCD:',
        'CC: \nHPI: \nPMH: \nMedications: \nPhysical Exam: \nPlan:',
        'MC: \nEA: \nAP: \nMedicamentos: \nExamen Físico: \nPlan:'
      )
    },
    {
      id: 'op_note_generic',
      title: t('Nota Operatória Genérica', 'Generic Operative Note', 'Nota Operatoria Genérica'),
      description: t('Modelo base para cirurgias.', 'Base template for surgeries.', 'Modelo base para cirugías.'),
      defaultText: t(
        'Procedimento: \nCirurgião: \nAnestesia: \nPosição: \nAcesso: \nAchados: \nProcedimento realizado: \nComplicações: Não houve. \nDesfecho: Estável.',
        'Procedure: \nSurgeon: \nAnesthesia: \nPosition: \nApproach: \nFindings: \nProcedure performed: \nComplications: None. \nOutcome: Stable.',
        'Procedimiento: \nCirujano: \nAnestesia: \nPosición: \nAbordaje: \nHallazgos: \nProcedimiento realizado: \nComplicaciones: Ninguna. \nDesenlace: Estable.'
      )
    },
    // --- IMAGING ---
    {
        id: 'imaging_req',
        title: t('Diagnóstico por Imagem', 'Diagnostic Imaging Request', 'Diagnóstico por Imagen'),
        description: t('Solicitação de RX, RM, TC, USG.', 'X-Ray, MRI, CT, Ultrasound requests.', 'Solicitud de RX, RM, TC, ECO.'),
        defaultText: t(
            'Solicitação de Exames:\n\n[ ] Raio-X (Incidências): \n[ ] Tomografia Computadorizada (Segmento): \n[ ] Ressonância Magnética (Segmento): \n[ ] Ultrassonografia: \n\nHipótese Diagnóstica / Indicação Clínica: ',
            'Imaging Request:\n\n[ ] X-Ray (Views): \n[ ] CT Scan (Segment): \n[ ] MRI (Segment): \n[ ] Ultrasound: \n\nClinical Indication: ',
            'Solicitud de Imagen:\n\n[ ] Radiografía (Proyecciones): \n[ ] Tomografía (Segmento): \n[ ] Resonancia (Segmento): \n[ ] Ecografía: \n\nIndicación Clínica: '
        )
    },
    // --- CONSERVATIVE ---
    {
        id: 'immobilization',
        title: t('Imobilização (Gesso/Tala)', 'Immobilization (Cast/Splint)', 'Inmovilización (Yeso/Férula)'),
        description: t('Aplicação de gesso, talas e cintas.', 'Application of casts and splints.', 'Aplicación de yesos y férulas.'),
        defaultText: t(
            'Procedimento: Imobilização\nTipo: \nMaterial (Gesso/Sintético): \nPosição da articulação: \nTempo estimado de uso: \nOrientações: Elevação, mobilização dos dedos, retorno se dor/edema excessivo.',
            'Procedure: Immobilization\nType: \nMaterial: \nJoint Position: \nDuration: \nInstructions: Elevation, finger motion, return if pain/swelling.',
            'Procedimiento: Inmovilización\nTipo: \nMaterial: \nPosición articular: \nDuración: \nInstrucciones: Elevación, movimiento de dedos.'
        )
    },
    {
        id: 'closed_reduction',
        title: t('Redução Fechada (Incruenta)', 'Closed Reduction', 'Reducción Cerrada'),
        description: t('Manipulação de ossos/articulações.', 'Manipulation of bones/joints.', 'Manipulación de huesos/articulaciones.'),
        defaultText: t(
            'Procedimento: Redução Incruenta\nDiagnóstico: \nAnalgesia/Sedação: \nManobra realizada: \nEstabilidade pós-redução: \nControle radioscópico: \nImobilização pós-redução: ',
            'Procedure: Closed Reduction\nDiagnosis: \nAnalgesia/Sedation: \nManeuver: \nStability: \nFluoroscopy: \nPost-reduction immobilization: ',
            'Procedimiento: Reducción Cerrada\nDiagnóstico: \nAnalgesia: \nManiobra: \nEstabilidad: \nFluoroscopia: \nInmovilización: '
        )
    },
    {
        id: 'injection',
        title: t('Infiltração Articular', 'Joint Injection', 'Infiltración Articular'),
        description: t('Aplicação intra-articular ou tendínea.', 'Intra-articular or tendon injection.', 'Aplicación intra-articular o tendinosa.'),
        defaultText: t(
            'Procedimento: Infiltração\nSítio anatômico: \nVia de acesso: \nAssepsia realizada.\nMedicamento (Corticóide/Anestésico/Viscossuplementação): \nVolume: \nCurativo oclusivo.',
            'Procedure: Injection\nSite: \nApproach: \nAsepsis performed.\nMedication: \nVolume: \nDressing.',
            'Procedimiento: Infiltración\nSitio: \nAbordaje: \nAsepsia realizada.\nMedicamento: \nVolumen: \nApósito.'
        )
    },
    {
        id: 'rehab_orient',
        title: t('Orientações de Reabilitação', 'Rehab Instructions', 'Instrucciones de Rehabilitación'),
        description: t('Fisioterapia e cuidados locais.', 'Physiotherapy and local care.', 'Fisioterapia y cuidados locales.'),
        defaultText: t(
            'Plano de Reabilitação:\n\n1. Crioterapia (Gelo): \n2. Repouso/Carga: \n3. Fisioterapia Motora: \n   - Objetivos: \n   - Frequência: \n4. Analgesia: ',
            'Rehab Plan:\n\n1. Cryotherapy: \n2. Rest/Weight Bearing: \n3. Physiotherapy: \n   - Goals: \n   - Frequency: \n4. Analgesia: ',
            'Plan de Rehabilitación:\n\n1. Crioterapia: \n2. Reposo/Carga: \n3. Fisioterapia: \n   - Objetivos: \n   - Frecuencia: \n4. Analgesia: '
        )
    },
    // --- SURGICAL ---
    {
        id: 'osteosynthesis',
        title: t('Osteossíntese (Fixação)', 'Osteosynthesis', 'Osteosíntesis'),
        description: t('Fixação de fraturas (Placas/Hastes).', 'Fracture fixation (Plates/Nails).', 'Fijación de fracturas (Placas/Clavos).'),
        defaultText: t(
            'Cirurgia: Osteossíntese\nDiagnóstico: \nAcesso cirúrgico: \nRedução (Aberta/Fechada): \nImplante utilizado (Placa/Haste/Parafusos): \nFixação (Técnica): \nEstabilidade final: \nFechamento por planos.',
            'Surgery: Osteosynthesis\nDiagnosis: \nApproach: \nReduction: \nImplant: \nFixation Technique: \nStability: \nClosure.',
            'Cirugía: Osteosíntesis\nDiagnóstico: \nAbordaje: \nReducción: \nImplante: \nTécnica: \nEstabilidad: \nCierre.'
        )
    },
    {
        id: 'arthroplasty',
        title: t('Artroplastia (Prótese)', 'Arthroplasty', 'Artroplastia'),
        description: t('Substituição articular (Quadril/Joelho).', 'Joint replacement (Hip/Knee).', 'Reemplazo articular (Cadera/Rodilla).'),
        defaultText: t(
            'Cirurgia: Artroplastia\nArticulação: \nAcesso: \nPreparo ósseo: \nComponentes (Acetabular/Femoral/Tibial): \nFixação (Cimentada/Não Cimentada): \nEstabilidade/Mobilidade intra-op: \nDreno: ',
            'Surgery: Arthroplasty\nJoint: \nApproach: \nBone prep: \nComponents: \nFixation: \nStability/ROM: \nDrain: ',
            'Cirugía: Artroplastia\nArticulación: \nAbordaje: \nPreparación ósea: \nComponentes: \nFijación: \nEstabilidad: \nDrenaje: '
        )
    },
    {
        id: 'arthroscopy',
        title: t('Artroscopia', 'Arthroscopy', 'Artroscopia'),
        description: t('Cirurgia minimamente invasiva.', 'Minimally invasive surgery.', 'Cirugía mínimamente invasiva.'),
        defaultText: t(
            'Cirurgia: Artroscopia\nArticulação: \nPortais: \nInventário articular: \nProcedimentos realizados (Meniscectomia/Reparo/Limpeza): \nComplicações: \nFechamento portais.',
            'Surgery: Arthroscopy\nJoint: \nPortals: \nJoint inventory: \nProcedures: \nComplications: \nClosure.',
            'Cirugía: Artroscopia\nArticulación: \nPortales: \nInventario: \nProcedimientos: \nComplicaciones: \nCierre.'
        )
    },
    {
        id: 'tendon_transfer',
        title: t('Transferência Tendinosa/Reparo', 'Tendon Transfer/Repair', 'Transferencia Tendinosa/Reparación'),
        description: t('Tratamento de tecidos moles.', 'Soft tissue treatment.', 'Tratamiento de tejidos blandos.'),
        defaultText: t(
            'Cirurgia: Reparo/Transferência Tendinosa\nLesão: \nAcesso: \nIdentificação dos cotos/tendões: \nTécnica de sutura/fixação (Âncora/Túnel): \nTensão: \nTeste funcional intra-op: ',
            'Surgery: Tendon Repair/Transfer\nInjury: \nApproach: \nIdentification: \nSuture/Fixation Technique: \nTension: \nFunctional Test: ',
            'Cirugía: Reparación/Transferencia\nLesión: \nAbordaje: \nIdentificación: \nTécnica: \nTensión: \nPrueba funcional: '
        )
    },
    {
        id: 'fasciotomy',
        title: t('Fasciotomia', 'Fasciotomy', 'Fasciotomía'),
        description: t('Síndrome compartimental.', 'Compartment syndrome.', 'Síndrome compartimental.'),
        defaultText: t(
            'Cirurgia: Fasciotomia Descompressiva\nIndicação: Síndrome Compartimental\nCompartimentos liberados: \nAspecto da musculatura (Contrátil/Necrose): \nDesbridamento realizado: \nCobertura (Vácuo/Curativo/Sutura elástica): ',
            'Surgery: Fasciotomy\nIndication: Compartment Syndrome\nCompartments Released: \nMuscle Viability: \nDebridement: \nCoverage: ',
            'Cirugía: Fasciotomía\nIndicación: Síndrome Compartimental\nCompartimentos: \nViabilidad Muscular: \nDesbridamiento: \nCobertura: '
        )
    },
    {
        id: 'bone_graft',
        title: t('Enxerto Ósseo', 'Bone Graft', 'Injerto Óseo'),
        description: t('Auxílio na união óssea.', 'Bone union aid.', 'Ayuda unión ósea.'),
        defaultText: t(
            'Procedimento: Enxertia Óssea\nIndicação (Pseudartrose/Defeito): \nTipo de Enxerto (Autólogo/Homólogo/Sintético): \nSítio doador: \nSítio receptor: \nPreparo do leito: \nFixação do enxerto: ',
            'Procedure: Bone Grafting\nIndication: \nGraft Type: \nDonor Site: \nRecipient Site: \nBed Prep: \nFixation: ',
            'Procedimiento: Injerto Óseo\nIndicación: \nTipo: \nSitio Donante: \nSitio Receptor: \nPreparación: \nFijación: '
        )
    },
    {
        id: 'amputation',
        title: t('Amputação', 'Amputation', 'Amputación'),
        description: t('Procedimento extremo.', 'Extreme procedure.', 'Procedimiento extremo.'),
        defaultText: t(
            'Cirurgia: Amputação\nNível: \nIncisão (Boca de peixe/Circular): \nTratamento vascular (Ligaduras): \nTratamento nervoso (Tração/Corte): \nTratamento ósseo (Limagem): \nMiodese/Mioplastia: \nFechamento/Dreno: ',
            'Surgery: Amputation\nLevel: \nIncision: \nVascular: \nNerve: \nBone: \nMuscle: \nClosure/Drain: ',
            'Cirugía: Amputación\nNivel: \nIncisión: \nVascular: \nNervioso: \nÓseo: \nMuscular: \nCierre/Drenaje: '
        )
    },
    // --- SUBSPECIALTIES ---
    {
        id: 'spine_surgery',
        title: t('Cirurgia de Coluna', 'Spine Surgery', 'Cirugía de Columna'),
        description: t('Hérnias, artrodeses.', 'Hernias, fusions.', 'Hernias, artrodesis.'),
        defaultText: t(
            'Cirurgia: Coluna\nNível: \nAcesso: \nDescompressão (Laminectomia/Discectomia): \nInstrumentação (Parafusos pediculares/Cages): \nArtrodese (Enxerto): \nNeuromonitorização: ',
            'Surgery: Spine\nLevel: \nApproach: \nDecompression: \nInstrumentation: \nFusion: \nNeuromonitoring: ',
            'Cirugía: Columna\nNivel: \nAbordaje: \nDescompresión: \nInstrumentación: \nArtrodesis: \nNeuromonitorización: '
        )
    },
    {
        id: 'hand_foot_surgery',
        title: t('Mão/Pé (Deformidades)', 'Hand/Foot Surgery', 'Cirugía Mano/Pie'),
        description: t('Correção, Túnel do Carpo.', 'Corrections, Carpal Tunnel.', 'Correcciones, Túnel Carpiano.'),
        defaultText: t(
            'Cirurgia: Mão/Pé\nDiagnóstico: \nProcedimento (Osteotomia/Liberação/Artrodese): \nAcesso: \nEstruturas abordadas: \nFixação: \nCurativo: ',
            'Surgery: Hand/Foot\nDiagnosis: \nProcedure: \nApproach: \nStructures: \nFixation: \nDressing: ',
            'Cirugía: Mano/Pie\nDiagnóstico: \nProcedimiento: \nAbordaje: \nEstructuras: \nFijación: \nApósito: '
        )
    },
    {
        id: 'oncology_ortho',
        title: t('Oncologia Ortopédica', 'Orthopedic Oncology', 'Oncología Ortopédica'),
        description: t('Biópsias e ressecções.', 'Biopsies and resections.', 'Biopsias y resecciones.'),
        defaultText: t(
            'Cirurgia: Oncologia\nTumor/Lesão: \nTipo (Biópsia/Ressecção): \nAcesso: \nMargens cirúrgicas: \nReconstrução (Endoprótese/Enxerto): \nMaterial enviado para patologia. ',
            'Surgery: Oncology\nTumor: \nType: \nApproach: \nMargins: \nReconstruction: \nPathology sample sent. ',
            'Cirugía: Oncología\nTumor: \nTipo: \nAbordaje: \nMárgenes: \nReconstrucción: \nMuestra enviada. '
        )
    },
    {
        id: 'pediatric_ortho',
        title: t('Ortopedia Pediátrica', 'Pediatric Orthopedics', 'Ortopedia Pediátrica'),
        description: t('Correção de deformidades.', 'Deformity correction.', 'Corrección de deformidades.'),
        defaultText: t(
            'Cirurgia: Pediátrica\nDiagnóstico: \nProcedimento (Tenotomia/Osteotomia/Redução): \nTécnica: \nFixação (Fios K/Gesso): \nCorreção obtida: ',
            'Surgery: Pediatric\nDiagnosis: \nProcedure: \nTechnique: \nFixation: \nCorrection: ',
            'Cirugía: Pediátrica\nDiagnóstico: \nProcedimiento: \nTécnica: \nFijación: \nCorrección: '
        )
    }
  ];
};

export const getClassifications = (lang: Language): Classification[] => {
  const isEn = lang === 'en';
  const isEs = lang === 'es';
  const t = <T extends any>(pt: T, en: T, es: T): T => {
    if (isEn) return en;
    if (isEs) return es;
    return pt;
  };

  return [
    // 1. AO/OTA Universal
    {
      id: 'ao_ota',
      name: t('AO/OTA (Universal)', 'AO/OTA (Universal)', 'AO/OTA (Universal)'),
      category: 'Geral',
      criteria: [
        { label: t('Osso', 'Bone', 'Hueso'), options: t(['Úmero', 'Rádio/Ulna', 'Fêmur', 'Tíbia'], ['Humerus', 'Radius/Ulna', 'Femur', 'Tibia'], ['Húmero', 'Radio/Cúbito', 'Fémur', 'Tibia']) },
        { label: t('Segmento', 'Segment', 'Segmento'), options: t(['Proximal (1)', 'Diáfise (2)', 'Distal (3)'], ['Proximal (1)', 'Diaphysis (2)', 'Distal (3)'], ['Proximal (1)', 'Diáfisis (2)', 'Distal (3)']) },
        { label: t('Tipo', 'Type', 'Tipo'), options: t(['A (Simples/Extra)', 'B (Cunha/Parcial)', 'C (Complexa/Total)'], ['A (Simple/Extra)', 'B (Wedge/Partial)', 'C (Complex/Total)'], ['A (Simple/Extra)', 'B (Cuña/Parcial)', 'C (Compleja/Total)']) }
      ],
      getResult: (selections) => {
        const [bone, segment, type] = selections;
        let boneCode = '0';
        if (bone.includes('mero')) boneCode = '1';
        if (bone.includes('Rádio')) boneCode = '2';
        if (bone.includes('Femur') || bone.includes('Fêmur') || bone.includes('Fémur')) boneCode = '3';
        if (bone.includes('Tibia') || bone.includes('Tíbia')) boneCode = '4';

        let segCode = '0';
        if (segment.includes('Proximal')) segCode = '1';
        if (segment.includes('Diáfise') || segment.includes('Diaphysis') || segment.includes('Diáfisis')) segCode = '2';
        if (segment.includes('Distal')) segCode = '3';

        let typeCode = 'A';
        if (type.includes('B')) typeCode = 'B';
        if (type.includes('C')) typeCode = 'C';

        const urgency = typeCode === 'C' ? 'High' : 'Medium';
        
        return {
            title: `AO/OTA ${boneCode}${segCode}-${typeCode}`,
            description: t(`Fratura do osso ${boneCode}, segmento ${segCode}, tipo ${typeCode}.`, `Fracture of bone ${boneCode}, segment ${segCode}, type ${typeCode}.`, `Fractura del hueso ${boneCode}, segmento ${segCode}, tipo ${typeCode}.`),
            action: typeCode === 'A' 
                ? t('Geralmente RAFI ou Haste (se diáfise).', 'Usually ORIF or Nail.', 'Generalmente RAFI o Clavo.')
                : typeCode === 'B' 
                    ? t('Requer redução anatômica (Parafusos de tração/Placas).', 'Requires anatomic reduction.', 'Requiere reducción anatómica.')
                    : t('Alta energia. Ponte óssea ou fixação híbrida. Cuidado com partes moles.', 'High energy. Bridge plating.', 'Alta energía. Placa puente.'),
            urgency: urgency,
            materials: ['Placa Bloqueada LCP', 'Haste Intramedular', 'Parafusos 3.5/4.5mm'],
            checklist: ['Planejamento 3D', 'Avaliar Neurovascular', 'Tração em Mesa']
        };
      }
    },

    // 2. Gustilo-Anderson (Trauma)
    {
      id: 'gustilo',
      name: 'Gustilo-Anderson (Fraturas Expostas)',
      category: 'Trauma',
      criteria: [
        { 
          label: t('Energia do Trauma', 'Trauma Energy', 'Energía del Trauma'), 
          options: t(['Baixa Energia', 'Alta Energia', 'Energia Extrema (Esmagamento)'], ['Low Energy', 'High Energy', 'Extreme Energy'], ['Baja Energía', 'Alta Energía', 'Energía Extrema']) 
        },
        { 
          label: t('Tamanho da Ferida', 'Wound Size', 'Tamaño de Herida'), 
          options: ['< 1cm', '1-10cm', '> 10cm'] 
        },
        { 
          label: t('Cobertura de Partes Moles', 'Soft Tissue Coverage', 'Cobertura de Tejidos'), 
          options: t(['Adequada', 'Inadequada (Desperiostização)', 'Lesão Vascular (Reparo)'], ['Adequate', 'Inadequate/Stripping', 'Vascular Injury'], ['Adecuada', 'Inadecuada', 'Lesión Vascular']) 
        }
      ],
      getResult: (selections) => {
        const [energy, size, tissue] = selections;
        const isVascular = tissue.includes('Vascular') || tissue.includes('Reparo');
        const isHighEnergy = energy.includes('Alta') || energy.includes('High') || energy.includes('Extrem');
        const isLarge = size === '> 10cm';
        const isCoveragePoor = tissue.includes('Inadequada') || tissue.includes('Inadequate');

        if (isVascular) {
          return {
            title: 'Grau IIIC',
            description: t('Lesão arterial com necessidade de reparo.', 'Arterial injury requiring repair.', 'Lesión arterial que requiere reparación.'),
            action: t('Emergência Vascular + Fixação Externa.', 'Vascular Emergency + Ex-Fix.', 'Emergencia Vascular + Fijación Externa.'),
            antibiotics: 'Cefazolina + Gentamicina + Penicilina/Metronidazol',
            antibioticDetails: { drug: 'Cefazolina', doseMgKg: 30, frequencyHours: 8, maxDoseMg: 2000 },
            urgency: 'Immediate',
            materials: ['Kit Fixador Externo', 'Kit Vascular', 'Soro 9L'],
            checklist: ['Doppler/Angio', 'Shunt Temporário', 'Estabilização Óssea', 'Cobertura VAC']
          };
        }
        if (isCoveragePoor || (isHighEnergy && isLarge)) {
          return {
            title: 'Grau IIIB',
            description: t('Perda extensa de cobertura, osso exposto.', 'Extensive soft tissue loss, exposed bone.', 'Pérdida extensa de cobertura.'),
            action: t('Desbridamento Seriado + Cobertura (Retalho).', 'Debridement + Flap.', 'Desbridamiento + Colgajo.'),
            antibiotics: 'Cefazolina + Gentamicina',
            antibioticDetails: { drug: 'Gentamicina', doseMgKg: 5, frequencyHours: 24, maxDoseMg: 240 },
            urgency: 'High',
            materials: ['Kit Fixador Externo', 'Curativo VAC', 'Soro 9L'],
            checklist: ['Desbridamento Radical', 'Second Look 48h', 'Planejar Retalho']
          };
        }
        if (isLarge || (isHighEnergy && !isCoveragePoor)) {
           return {
            title: 'Grau IIIA',
            description: t('Laceração >10cm, cobertura óssea possível.', 'Laceration >10cm, coverage possible.', 'Laceración >10cm, cobertura posible.'),
            action: t('Desbridamento + Fixação Definitiva se limpa.', 'Debridement + Fixation.', 'Desbridamiento + Fijación.'),
            antibiotics: 'Cefazolina + Gentamicina',
            antibioticDetails: { drug: 'Cefazolina', doseMgKg: 30, frequencyHours: 8, maxDoseMg: 2000 },
            urgency: 'High',
            materials: ['Haste Intramedular', 'Soro 6L'],
            checklist: ['Desbridamento', 'Lavagem 6L', 'Fechamento primário tardio']
          };
        }
        if (size === '1-10cm') {
          return {
            title: 'Grau II',
            description: t('Ferida 1-10cm, contusão moderada.', 'Wound 1-10cm, moderate contusion.', 'Herida 1-10cm, contusión moderada.'),
            action: t('Desbridamento + Estabilização.', 'Debridement + Stabilization.', 'Desbridamiento + Estabilización.'),
            antibiotics: 'Cefazolina',
            antibioticDetails: { drug: 'Cefazolina', doseMgKg: 30, frequencyHours: 8, maxDoseMg: 2000 },
            urgency: 'Medium',
            materials: ['Haste/Placa', 'Soro 6L'],
            checklist: ['Ampliação da ferida', 'Lavagem mecânica']
          };
        }
        return {
          title: 'Grau I',
          description: t('Ferida <1cm, puntiforme, limpa.', 'Wound <1cm, clean.', 'Herida <1cm, limpia.'),
          action: t('Curativo + Fixação Padrão.', 'Wound care + Fixation.', 'Curación + Fijación.'),
          antibiotics: 'Cefazolina',
          antibioticDetails: { drug: 'Cefazolina', doseMgKg: 30, frequencyHours: 8, maxDoseMg: 2000 },
          urgency: 'Medium',
          materials: ['Haste/Placa', 'Soro 3L'],
          checklist: ['Limpeza local', 'Sutura primária', 'Tétano']
        };
      }
    },

    // 3. Garden (Femoral Neck)
    {
      id: 'garden',
      name: t('Garden (Colo do Fêmur)', 'Garden (Femoral Neck)', 'Garden (Cuello Femoral)'),
      category: 'Quadril',
      criteria: [
        { label: t('Integridade da Fratura', 'Fracture Integrity', 'Integridad'), options: t(['Incompleta/Impactada', 'Completa'], ['Incomplete/Impacted', 'Complete'], ['Incompleta', 'Completa']) },
        { label: t('Alinhamento Trabecular', 'Trabecular Alignment', 'Alineación'), options: t(['Valgo/Alinhado', 'Desvio Varo Parcial', 'Desvio Total (Perda de Contato)'], ['Valgus/Aligned', 'Partial Varus', 'Total Displacement'], ['Valgo/Alineado', 'Varo Parcial', 'Desplazamiento Total']) }
      ],
      getResult: (selections) => {
        const [integrity, alignment] = selections;
        
        if (alignment.includes('Total')) return {
            title: 'Garden IV',
            description: t('Completa com desvio total. Trabéculas paralelas (cabeça solta). Alto risco de necrose.', 'Complete, fully displaced. High AVN risk.', 'Completa, desplazamiento total.'),
            action: t('Idosos: Artroplastia (Hemi/Total). Jovens: Redução Urgente + Fixação.', 'Elderly: Arthroplasty. Young: Urgent Fixation.', 'Ancianos: Artroplastia. Jóvenes: Fijación Urgente.'),
            urgency: 'Immediate',
            materials: ['Prótese de Quadril', 'Parafusos Canulados 7.3mm'],
            checklist: ['Jejum', 'Reserva de Sangue', 'Risco Cardiológico']
        };
        if (alignment.includes('Parcial')) return {
            title: 'Garden III',
            description: t('Completa com desvio parcial em Varo.', 'Complete, partially displaced (Varus).', 'Completa, desplazamiento parcial.'),
            action: t('Idosos: Artroplastia. Jovens: Redução + Fixação.', 'Elderly: Arthroplasty. Young: Fixation.', 'Ancianos: Artroplastia. Jóvenes: Fijación.'),
            urgency: 'High',
            materials: ['Prótese de Quadril', 'DHS / Canulados'],
            checklist: ['Avaliar estoque ósseo']
        };
        if (integrity.includes('Completa')) return {
            title: 'Garden II',
            description: t('Completa sem desvio. Trabéculas interrompidas mas alinhadas.', 'Complete, non-displaced.', 'Completa, no desplazada.'),
            action: t('Fixação In Situ (Parafusos Canulados).', 'Fixation In Situ.', 'Fijación In Situ.'),
            urgency: 'Medium',
            materials: ['Parafusos Canulados 7.3mm (x3)'],
            checklist: ['Posicionamento Mesa', 'Intensificador']
        };
        return {
            title: 'Garden I',
            description: t('Incompleta ou Impactada em Valgo.', 'Incomplete or Valgus Impacted.', 'Incompleta o Impactada.'),
            action: t('Fixação In Situ (Prevenção de desvio).', 'Fixation In Situ.', 'Fijación In Situ.'),
            urgency: 'Medium',
            materials: ['Parafusos Canulados 7.3mm (x3)'],
            checklist: ['Carga parcial protegida']
        };
      }
    },

    // 4. Schatzker (Planalto Tibial)
    {
      id: 'schatzker',
      name: t('Schatzker (Platô Tibial)', 'Schatzker (Tibial Plateau)', 'Schatzker (Platillo Tibial)'),
      category: 'Joelho',
      criteria: [
        { label: t('Padrão da Fratura', 'Fracture Pattern', 'Patrón de Fractura'), options: t([
            'I: Cisalhamento Lateral (Split)', 
            'II: Cisalhamento + Depressão', 
            'III: Depressão Pura', 
            'IV: Platô Medial', 
            'V: Bicondilar', 
            'VI: Dissociação Metafisária'
          ], [
            'I: Split Wedge', 
            'II: Split + Depression', 
            'III: Pure Depression', 
            'IV: Medial Plateau', 
            'V: Bicondylar', 
            'VI: Metaphyseal Dissociation'
          ], [
            'I: Cizallamiento Lateral', 
            'II: Cizallamiento + Depresión', 
            'III: Depresión Pura', 
            'IV: Platillo Medial', 
            'V: Bicondílea', 
            'VI: Disociación Metafisaria'
          ]) 
        }
      ],
      getResult: (selections) => {
        const [pattern] = selections;
        
        if (pattern.includes('VI') || pattern.includes('Dissociação') || pattern.includes('Dissociation')) {
            return {
                title: 'Schatzker VI',
                description: t('Separação completa dos côndilos da diáfise. Lesão de alta energia.', 'Complete separation from diaphysis. High energy.', 'Separación completa de diáfisis.'),
                action: t('Damage Control (Fixador Externo) -> RAFI Definitiva (Placa Dupla).', 'Damage Control (Ex-Fix) -> Dual Plating.', 'Control de Daños -> Doble Placa.'),
                urgency: 'High',
                materials: ['Fixador Externo', 'Placas Bloqueadas 3.5/4.5mm'],
                checklist: ['Sinais Compartimentais', 'TC 3D', 'Planejar Vias']
            };
        }
        if (pattern.includes('V') || pattern.includes('Bicond')) {
            return {
                title: 'Schatzker V',
                description: t('Fratura bicondilar (Y invertido). Metáfise e diáfise ainda conectadas.', 'Bicondylar fracture.', 'Fractura bicondílea.'),
                action: t('Placa Dupla (Medial + Lateral) ou Placa Lateral + Parafusos Mediais.', 'Dual Plating or Lateral Plate + Medial Screws.', 'Doble Placa.'),
                urgency: 'High',
                materials: ['Placa Bloqueada Lateral', 'Placa de Suporte Medial'],
                checklist: ['TC Obrigatória', 'Monitorar pele']
            };
        }
        if (pattern.includes('IV') || pattern.includes('Medial')) {
            return {
                title: 'Schatzker IV',
                description: t('Fratura do platô medial. Associada a alta energia e lesão neurovascular.', 'Medial plateau. High energy risk.', 'Platillo medial.'),
                action: t('RAFI com Placa de Suporte Medial (Buttress).', 'ORIF with Medial Buttress Plate.', 'RAFI con Placa de Soporte.'),
                urgency: 'High',
                materials: ['Placa T ou L 4.5mm'],
                checklist: ['Avaliar nervo fibular', 'Artéria poplítea']
            };
        }
        if (pattern.includes('III') || pattern.includes('Depress')) {
            return {
                title: 'Schatzker III',
                description: t('Depressão central pura. Comum em idosos/osteoporose.', 'Pure central depression.', 'Depresión pura.'),
                action: t('Elevação do fragmento + Enxerto + Parafusos/Placa (Rafting).', 'Elevation + Graft + Rafting Screws.', 'Elevación + Injerto + Tornillos.'),
                urgency: 'Medium',
                materials: ['Enxerto Ósseo', 'Parafusos 6.5mm'],
                checklist: ['Janela cortical', 'Fluoroscopia']
            };
        }
        return { // Covers I and II
            title: 'Schatzker I / II',
            description: t('Fratura do platô lateral (Cisalhamento puro ou com afundamento).', 'Lateral plateau split/depression.', 'Platillo lateral.'),
            action: t('RAFI com Placa de Suporte Lateral + Elevação se afundado.', 'ORIF Lateral Buttress Plate.', 'RAFI Placa Lateral.'),
            urgency: 'Medium',
            materials: ['Placa Bloqueada Tíbia Proximal'],
            checklist: ['Menisco lateral (reparo?)']
        };
      }
    },

    // 5. Danis-Weber (Ankle)
    {
      id: 'danis_weber',
      name: t('Danis-Weber (Tornozelo)', 'Danis-Weber (Ankle)', 'Danis-Weber (Tobillo)'),
      category: 'Tornozelo',
      criteria: [
        { label: t('Nível da Fratura Fibular', 'Fibula Fracture Level', 'Nivel Fractura Peroné'), options: t([
            'A: Infra-sindesmal (Avulsão)', 
            'B: Trans-sindesmal (Espiral)', 
            'C: Supra-sindesmal (Alta)'
          ], [
            'A: Infra-syndesmotic', 
            'B: Trans-syndesmotic', 
            'C: Supra-syndesmotic'
          ], [
            'A: Infra-sindesmal', 
            'B: Trans-sindesmal', 
            'C: Supra-sindesmal'
          ]) 
        },
        { label: t('Mecanismo (Lauge-Hansen)', 'Mechanism', 'Mecanismo'), options: t(['Inversão/Adução', 'Eversão/Rotação Externa'], ['Inversion/Adduction', 'Eversion/External Rotation'], ['Inversión', 'Eversión']) }
      ],
      getResult: (selections) => {
        const [level, mech] = selections;
        
        if (level.includes('C:')) {
            return {
                title: 'Weber C (Pronação-Rotação Ext.)',
                description: t('Fratura da fíbula acima da sindesmose. Lesão sindesmal obrigatória (Instável).', 'Above syndesmosis. Syndesmosis injury mandated.', 'Supra-sindesmal. Lesión sindesmal obligatoria.'),
                action: t('RAFI Fíbula + Fixação da Sindesmose (Parafuso ou Tightrope).', 'ORIF Fibula + Syndesmosis Fixation.', 'RAFI Peroné + Fijación Sindesmal.'),
                urgency: 'High',
                materials: ['Placa 1/3 Tubo', 'Parafuso Sindesmose 3.5/4.5mm'],
                checklist: ['Teste de Cotton', 'Explorar Deltóide?']
            };
        }
        if (level.includes('B:')) {
            return {
                title: 'Weber B (Supinação-Rotação Ext.)',
                description: t('Ao nível da sindesmose. 50% de risco de lesão sindesmal.', 'At syndesmosis level. 50% syndesmosis risk.', 'Al nivel de la sindesmosis.'),
                action: t('RAFI Fíbula. Testar Sindesmose intra-op. Se estável: carga precoce.', 'ORIF Fibula. Stress test syndesmosis.', 'RAFI Peroné. Test de Estrés.'),
                urgency: 'Medium',
                materials: ['Placa 1/3 Tubo', 'Parafusos Lag 3.5mm'],
                checklist: ['Estabilidade medial', 'Rx Stress']
            };
        }
        return {
            title: 'Weber A (Supinação-Adução)',
            description: t('Abaixo da sindesmose. Sindesmose íntegra. Geralmente avulsão.', 'Below syndesmosis. Stable.', 'Infra-sindesmal. Estable.'),
            action: t('Conservador (Bota gessada/Robofoot) se sem desvio. Cirurgia se desvio > 2mm.', 'Conservative (Boot/Cast).', 'Conservador (Bota).'),
            urgency: 'Low',
            materials: ['Bota Imobilizadora'],
            checklist: ['Carga conforme dor']
        };
      }
    },

    // 6. Salter-Harris (Pediatric)
    {
      id: 'salter_harris',
      name: 'Salter-Harris (Fisária)',
      category: 'Pediátrica',
      criteria: [
        { label: t('Traço de Fratura', 'Fracture Line', 'Línea de Fractura'), options: t(['Através da Fise (Pura)', 'Fise + Metáfise (Thurston-Holland)', 'Fise + Epífise (Articular)', 'Atravessa Tudo (Meta-Fise-Epífise)', 'Compressão Axial (Esmagamento)'], ['Through Physis', 'Physis + Metaphysis', 'Physis + Epiphysis', 'Through All', 'Crush'], ['A través de Fisis', 'Fisis + Metáfisis', 'Fisis + Epífisis', 'A través de todo', 'Compresión']) }
      ],
      getResult: (selections) => {
        const [type] = selections;
        
        if (type.includes('Compressão') || type.includes('Crush')) return {
            title: 'Tipo V (ER - Erasure)',
            description: t('Compressão da placa fisária. Difícil diagnóstico inicial. Pior prognóstico.', 'Physeal crush injury. Poor prognosis.', 'Compresión de la fisis.'),
            action: t('Imobilização + Acompanhamento rigoroso (Risco de parada de crescimento).', 'Cast + Strict follow-up.', 'Inmovilización + Seguimiento.'),
            urgency: 'Medium',
            materials: ['Gesso'],
            checklist: ['Alertar pais sobre prognóstico', 'RNM se dúvida']
        };
        if (type.includes('Atravessa Tudo') || type.includes('Through All')) return {
            title: 'Tipo IV (T - Through)',
            description: t('Intra-articular. Cruza metáfise, fise e epífise.', 'Intra-articular. Crosses all layers.', 'Intra-articular.'),
            action: t('Redução Anatômica Aberta + Fixação Interna.', 'Open Reduction Internal Fixation.', 'RAFI.'),
            urgency: 'High',
            materials: ['Parafusos Canulados Pequenos', 'Fios K'],
            checklist: ['Não cruzar fise com rosca', 'Redução perfeita']
        };
        if (type.includes('Epífise')) return {
            title: 'Tipo III (L - Lower)',
            description: t('Intra-articular. Fise até a epífise.', 'Intra-articular. Physis to epiphysis.', 'Intra-articular.'),
            action: t('Redução Anatômica (frequentemente cirúrgica).', 'Anatomic Reduction.', 'Reducción Anatómica.'),
            urgency: 'High',
            materials: ['Parafusos ou Fios'],
            checklist: ['Restaurar superfície articular']
        };
        if (type.includes('Metáfise')) return {
            title: 'Tipo II (A - Above)',
            description: t('Fise + fragmento metafisário triangular. Mais comum (75%).', 'Physis + Metaphysis. Most common.', 'Fisis + Metáfisis.'),
            action: t('Redução Fechada + Gesso (Cirurgia se interposição).', 'Closed Reduction + Cast.', 'Reducción Cerrada + Yeso.'),
            urgency: 'Medium',
            materials: ['Gesso', 'Fios K (opcional)'],
            checklist: ['Manobra suave']
        };
        return {
            title: 'Tipo I (S - Separated)',
            description: t('Descolamento epifisário puro.', 'Pure epiphyseal separation.', 'Separación epifisaria pura.'),
            action: t('Imobilização gessada.', 'Cast immobilization.', 'Inmovilización.'),
            urgency: 'Low',
            materials: ['Gesso'],
            checklist: ['Rx Stress se dúvida']
        };
      }
    },

    // 7. Neer (Proximal Humerus)
    {
      id: 'neer',
      name: t('Neer (Úmero Proximal)', 'Neer (Prox Humerus)', 'Neer (Húmero Proximal)'),
      category: 'Ombro',
      criteria: [
        { label: t('Desvio Significativo (>1cm ou 45°)', 'Displacement', 'Desplazamiento'), options: t(['Não', 'Sim'], ['No', 'Yes'], ['No', 'Sí']) },
        { label: t('Segmentos Acometidos', 'Segments Involved', 'Segmentos Afectados'), options: t(['Nenhum', 'Colo Cirúrgico', 'Colo + Tubérculo Maior', 'Colo + Tubérculo Menor', 'Colo + Ambos Tubérculos'], ['None', 'Surgical Neck', 'Neck + GT', 'Neck + LT', 'Neck + Both Tuberosities'], ['Ninguno', 'Cuello Quirúrgico', 'Cuello + Troquiter', 'Cuello + Troquín', 'Cuello + Ambos']) }
      ],
      getResult: (selections) => {
        const [displaced, segments] = selections;
        
        if (displaced === 'Não' || displaced === 'No') return {
            title: 'Neer 1 Parte',
            description: t('Fratura sem desvio significativo, independente do número de traços.', 'No significant displacement.', 'Sin desplazamiento significativo.'),
            action: t('Conservador (Tipoia Velpeau).', 'Conservative (Sling).', 'Conservador.'),
            urgency: 'Low',
            materials: ['Tipoia Velpeau'],
            checklist: ['Rx Controle 1 semana', 'Pendular precoce']
        };

        if (segments.includes('Ambos') || segments.includes('Both')) return {
            title: 'Neer 4 Partes',
            description: t('Colo cirúrgico + Tubérculo Maior + Menor. Cabeça desconectada. Alto risco de necrose.', 'All segments displaced. High AVN risk.', '4 partes. Alto riesgo necrosis.'),
            action: t('Jovens: Tentar Fixação. Idosos: Artroplastia Reversa.', 'Young: Fixation attempt. Elderly: Reverse Arthroplasty.', 'Jóvenes: Fijación. Ancianos: Artroplastia Reversa.'),
            urgency: 'High',
            materials: ['Placa Bloqueada (Philos)', 'Prótese Reversa'],
            checklist: ['Suturas Manguito', 'Enxerto Ósseo']
        };

        if (segments.includes('Tubérculo') || segments.includes('GT') || segments.includes('LT')) return {
            title: 'Neer 3 Partes',
            description: t('Colo cirúrgico + Um dos tubérculos deslocados.', 'Surgical neck + one tuberosity.', 'Cuello + una tuberosidad.'),
            action: t('Cirúrgico: Redução + Fixação (Placa Bloqueada).', 'Surgical: ORIF.', 'Quirúrgico: RAFI.'),
            urgency: 'Medium',
            materials: ['Placa Bloqueada Proximal', 'Suturas Alta Resistência'],
            checklist: ['Amarra dos tubérculos']
        };

        return {
            title: 'Neer 2 Partes',
            description: t('Desvio isolado do colo cirúrgico (mais comum) ou anatômico.', 'Isolated surgical neck displacement.', 'Desplazamiento aislado del cuello.'),
            action: t('Tentar Redução Fechada. Se instável: Fixação (Haste ou Placa).', 'Closed reduction attempt. If unstable: ORIF.', 'Reducción cerrada. Si inestable: Fijación.'),
            urgency: 'Medium',
            materials: ['Haste Umeral', 'Placa Bloqueada'],
            checklist: ['Avaliar nervo axilar']
        };
      }
    },

    // 8. Rockwood (AC Joint - Shoulder)
    {
      id: 'rockwood',
      name: t('Luxação Acromioclavicular (Rockwood)', 'AC Joint Dislocation (Rockwood)', 'Luxación Acromioclavicular (Rockwood)'),
      category: 'Ombro',
      criteria: [
        { label: t('Integridade Ligamentar', 'Ligament Integrity', 'Integridad Ligamentaria'), options: t(['Estiramento', 'Ruptura AC', 'Ruptura AC + CC'], ['Sprain', 'AC Tear', 'AC + CC Tear'], ['Esguince', 'Ruptura AC', 'Ruptura AC + CC']) },
        { label: t('Deslocamento Vertical', 'Vertical Displacement', 'Desplazamiento Vertical'), options: t(['Nenhum', 'Subluxação (<50%)', 'Luxação (100%)', 'Extremo (>100-300%)'], ['None', 'Subluxation', 'Dislocation', 'Extreme'], ['Ninguno', 'Subluxación', 'Luxación', 'Extremo']) },
        { label: t('Direção Atípica', 'Atypical Direction', 'Dirección Atípica'), options: t(['Não (Padrão)', 'Posterior (no Trapézio)', 'Inferior (Subcoracoide)'], ['No (Standard)', 'Posterior', 'Inferior'], ['No (Estándar)', 'Posterior', 'Inferior']) }
      ],
      getResult: (selections) => {
        const [ligaments, displacement, direction] = selections;
        
        if (direction.includes('Posterior')) return {
            title: 'Tipo IV',
            description: t('Clavícula deslocada posteriormente através do trapézio.', 'Clavicle displaced posteriorly through trapezius.', 'Clavícula desplazada posteriormente.'),
            action: t('Cirúrgico (Redução Aberta + Fixação).', 'Surgical (ORIF).', 'Quirúrgico.'),
            urgency: 'High',
            materials: ['Âncora/Button', 'Fios Kirschner'],
            checklist: ['Avaliar pele', 'RX Axilar']
        };
        if (direction.includes('Inferior')) return {
            title: 'Tipo VI',
            description: t('Luxação inferior (subacromial/subcoracoide). Rara.', 'Inferior dislocation. Rare.', 'Luxación inferior.'),
            action: t('Cirúrgico Imediato.', 'Immediate Surgery.', 'Quirúrgico Inmediato.'),
            urgency: 'Immediate',
            materials: ['Material de Revisão'],
            checklist: ['Neurovascular', 'TC Ombro']
        };
        if (displacement.includes('Extrem')) return {
            title: 'Tipo V',
            description: t('Elevação grave (>100-300%). Desinserção delto-trapezoidal.', 'Severe elevation. Fascial stripping.', 'Elevación severa.'),
            action: t('Cirúrgico (Reconstrução Ligamentar).', 'Surgical (Ligament Recon).', 'Quirúrgico.'),
            urgency: 'Medium',
            materials: ['Tightrope/Endobutton', 'Enxerto (se crônico)'],
            checklist: ['Avaliar pele (risco necrose)']
        };
        if (displacement.includes('100%')) return {
            title: 'Tipo III',
            description: t('Ruptura completa AC e CC. Clavícula elevada.', 'Complete AC/CC tear.', 'Ruptura completa AC/CC.'),
            action: t('Controverso: Conservador na maioria; Cirúrgico em atletas/braçais.', 'Conservative usually; Surgical for athletes.', 'Conservador general; Quirúrgico atletas.'),
            urgency: 'Medium',
            materials: ['Tipoia'],
            checklist: ['Discutir demanda funcional', 'Rx Comparativo']
        };
        if (ligaments.includes('Ruptura AC')) return {
            title: 'Tipo II',
            description: t('Ruptura AC, estiramento CC. Instabilidade horizontal.', 'AC tear, CC sprain.', 'Ruptura AC, esguince CC.'),
            action: t('Conservador (Gelo + Tipoia).', 'Conservative.', 'Conservador.'),
            urgency: 'Low',
            materials: ['Tipoia Simples'],
            checklist: ['Analgesia', 'Reavaliação 2 sem']
        };
        return {
            title: 'Tipo I',
            description: t('Entorse AC. RX Normal.', 'AC Sprain. Normal X-Ray.', 'Esguince AC.'),
            action: t('Sintomático (Repouso).', 'Symptomatic.', 'Sintomático.'),
            urgency: 'Low',
            materials: ['Gelo'],
            checklist: ['Retorno conforme dor']
        };
      }
    },

    // 9. Fernandez (Distal Radius)
    {
      id: 'fernandez',
      name: t('Fernandez (Rádio Distal)', 'Fernandez (Distal Radius)', 'Fernandez (Radio Distal)'),
      category: 'Punho',
      criteria: [
        { label: t('Mecanismo de Lesão', 'Injury Mechanism', 'Mecanismo'), options: t([
            'I: Flexão (Colles/Smith)', 
            'II: Cisalhamento (Barton)', 
            'III: Compressão (Die-punch)', 
            'IV: Avulsão (Chauffeur)', 
            'V: Alta Energia (Combinada)'
          ], [
            'I: Bending', 
            'II: Shearing', 
            'III: Compression', 
            'IV: Avulsion', 
            'V: Combined'
          ], [
            'I: Flexión', 
            'II: Cizallamiento', 
            'III: Compresión', 
            'IV: Avulsión', 
            'V: Combinada'
          ]) 
        }
      ],
      getResult: (selections) => {
        const [mech] = selections;
        
        if (mech.includes('V:') || mech.includes('Alta Energia') || mech.includes('Combined')) {
            return {
                title: 'Tipo V (Explosão)',
                description: t('Fratura complexa de alta energia. Múltiplos fragmentos.', 'High energy complex fracture.', 'Fractura compleja alta energía.'),
                action: t('Fixador Externo (Distração) + Placa Volar posteriormente.', 'Ex-Fix + Volar Plate.', 'Tutor Externo + Placa.'),
                urgency: 'High',
                materials: ['Fixador Externo', 'Placa Volar', 'Fios K'],
                checklist: ['Túnel do Carpo', 'Partes Moles']
            };
        }
        if (mech.includes('II:') || mech.includes('Barton')) {
            return {
                title: 'Tipo II (Cisalhamento)',
                description: t('Fratura-luxação articular (Barton). Instável.', 'Shearing fracture-dislocation.', 'Fractura-luxación.'),
                action: t('RAFI com Placa de Suporte (Volar ou Dorsal dependendo do desvio).', 'ORIF with Buttress Plate.', 'RAFI con Placa de Soporte.'),
                urgency: 'Medium',
                materials: ['Placa Volar Bloqueada'],
                checklist: ['Redução Articular Perfeita']
            };
        }
        if (mech.includes('III:') || mech.includes('Die-punch')) {
            return {
                title: 'Tipo III (Compressão)',
                description: t('Afundamento da faceta articular (Die-Punch).', 'Articular depression.', 'Hundimiento articular.'),
                action: t('Redução Artroscópica ou Aberta + Enxerto.', 'Arthroscopic/Open Reduction + Graft.', 'Reducción + Injerto.'),
                urgency: 'Medium',
                materials: ['Enxerto Ósseo', 'Parafusos/Placa'],
                checklist: ['Degrau articular < 1mm']
            };
        }
        return {
            title: 'Tipo I (Flexão)',
            description: t('Fratura metafisária extra-articular (Colles ou Smith).', 'Extra-articular metaphyseal.', 'Metafisaria extra-articular.'),
            action: t('Redução Fechada + Gesso (Se estável). RAFI se instável.', 'CR + Cast (if stable). ORIF if unstable.', 'RC + Yeso o RAFI.'),
            urgency: 'Low',
            materials: ['Gesso', 'Placa Volar (opcional)'],
            checklist: ['Índices radiológicos']
        };
      }
    },

    // 10. Judet-Letournel (Acetabulum)
    {
      id: 'judet_letournel',
      name: t('Judet-Letournel (Acetábulo)', 'Judet-Letournel (Acetabulum)', 'Judet-Letournel (Acetábulo)'),
      category: 'Pelve',
      criteria: [
        { label: t('Padrão da Fratura', 'Fracture Pattern', 'Patrón'), options: t([
            'Parede Posterior (Mais comum)', 
            'Coluna Posterior', 
            'Parede Anterior', 
            'Coluna Anterior', 
            'Transversa',
            'Duas Colunas (Associada)',
            'Em T (Associada)'
          ], [
            'Posterior Wall', 
            'Posterior Column', 
            'Anterior Wall', 
            'Anterior Column', 
            'Transverse',
            'Both Columns',
            'T-Type'
          ], [
            'Pared Posterior', 
            'Columna Posterior', 
            'Pared Anterior', 
            'Columna Anterior', 
            'Transversa',
            'Dos Columnas',
            'En T'
          ]) 
        }
      ],
      getResult: (selections) => {
        const [pattern] = selections;
        
        if (pattern.includes('Duas Colunas') || pattern.includes('Both Columns') || pattern.includes('Dos Columnas')) {
            return {
                title: 'Fratura de Duas Colunas',
                description: t('Sinal do Esporão (Spur Sign). Dissociação completa do acetábulo do esqueleto axial.', 'Spur Sign. Complete dissociation.', 'Signo del Espolón. Disociación completa.'),
                action: t('Acesso Ilioinguinal ou Stoppa Modificado.', 'Ilioinguinal or Stoppa approach.', 'Abordaje Ilioinguinal o Stoppa.'),
                urgency: 'High',
                materials: ['Placas de Reconstrução 3.5mm (x2)', 'Parafusos Longos'],
                checklist: ['Embolização pré-op?', 'Tração esquelética']
            };
        }
        if (pattern.includes('Parede Posterior') || pattern.includes('Posterior Wall')) {
            return {
                title: 'Fratura da Parede Posterior',
                description: t('Tipo mais comum (25%). Frequentemente associada a luxação posterior do quadril.', 'Most common. Often with hip dislocation.', 'Más común. Luxación posterior asociada.'),
                action: t('Acesso de Kocher-Langenbeck. Fixação com placa mola ou reconstrução.', 'Kocher-Langenbeck approach.', 'Abordaje Kocher-Langenbeck.'),
                urgency: 'Medium',
                materials: ['Placa de Reconstrução', 'Parafusos corticais'],
                checklist: ['Nervo Ciático', 'Redução da luxação']
            };
        }
        if (pattern.includes('Transversa') || pattern.includes('Transverse')) {
            return {
                title: 'Fratura Transversa',
                description: t('Separa o hemipelvis em superior e inferior. Envolve as duas colunas.', 'Separates hemipelvis into Sup/Inf.', 'Separa hemipelvis.'),
                action: t('Depende da altura. K-L, Ilioinguinal ou ambos.', 'Depends on height. K-L or Ilioinguinal.', 'K-L o Ilioinguinal.'),
                urgency: 'High',
                materials: ['Placas Curvas'],
                checklist: ['Planejamento 3D Obrigatório']
            };
        }
        return {
            title: 'Fratura da Coluna Anterior',
            description: t('Traço baixo (Iliopectíneo).', 'Low fracture line.', 'Trazo bajo.'),
            action: t('Acesso Ilioinguinal.', 'Ilioinguinal approach.', 'Abordaje Ilioinguinal.'),
            urgency: 'Medium',
            materials: ['Placa de Reconstrução'],
            checklist: ['Nervo Femoral Cutâneo Lateral']
        };
      }
    }
  ];
};

export const SURGICAL_TEMPLATES = getSurgicalTemplates('pt');
